// src/components/CreateProjectModal.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, GripVertical, MoreHorizontal, Plus, Pencil, ArrowRightLeft, Trash2, Loader2, Info, Check } from 'lucide-react';
import { getCookie } from '../utils/utils';
import GatheringInfoToast from './GatheringInfoToast';
import CommercialProductList from './CommercialProductList';
import { queryClient } from '../queryClient';
import {
  WorkspaceKey,
  MarketplaceProduct,
  marketplaceProductsQuery,
} from '../utils/marketplaceProducts';
import { useCaps } from '../contexts/CapabilitiesContext';

interface Country {
  id: number;
  name: string;
  alpha_2: string;
  alpha_3: string;
}

export interface ProjectCreatedPayload {
  id: number;
  name: string;
  markets?: string[];
  opportunity_space?: string;
  gather_project_info?: boolean;
  lifecycle?: 'draft' | 'active';
  research_start_warning?: string;
  research_jobs?: unknown[];
  research_job?: unknown;
  [key: string]: unknown;
}

interface InnovationPlatform {
  id: number;
  job_to_be_done: string;
  tension: string;
  market: string;
  has_been_evidenced?: boolean;
  job_score?: number;
  tension_score?: number;
  created_by_email?: string;
  created_at?: string;
}

interface JobTensionItem {
  id: string;
  text: string;
  source: 'catalyx-ai' | 'user';
  score: number;
  deepDive?: string;
  isInnovationPlatform?: boolean;
  platformLinked?: boolean;
  evidenced?: boolean;
  userTensionId?: number;
}

interface JobItem {
  id: string;
  text: string;
  source: 'catalyx-ai' | 'user';
  evidenced: boolean;
  score: number;
  platformId?: number;
  market?: string;
  addedBy?: string;
  createdOn?: string;
  tensions: JobTensionItem[];
  tensionCount: number;
}

interface PlatformConceptLink {
  combinationId: number;
  artifactId: number;
  label: string;
}

export type WorkspaceDrawerStep = 'platform-form' | 'platform-list';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated?: (project: ProjectCreatedPayload) => void;
  /** Fired right after the project is created (Continue on step 1) so the main view can open the recipe workflow. */
  onProjectContinue?: (project: ProjectCreatedPayload) => void;
  /** Called after jobs, tensions, or platforms change in workspace mode. */
  onWorkspaceDataChange?: () => void;
  /** When `workspace`, opens Innovation Workspace for an existing project (skips create step). */
  mode?: 'create' | 'workspace';
  existingProjectId?: number | null;
  /** Preload workspace after create-project continue (used by ProjectWorkflowView). */
  bootstrapProject?: ProjectCreatedPayload | null;
  initialDrawerStep?: WorkspaceDrawerStep;
  highlightedJobId?: string | null;
  highlightedJobText?: string | null;
  workspaceTabRequest?: WorkspaceTab | null;
  onWorkspaceTabRequestConsumed?: () => void;
  onViewWorkspace?: (platformId: number) => void;
  onViewJob?: (jobId: string) => void;
  onViewConcept?: (platformId: number, combinationId: number, artifactId: number) => void;
  onNavigateToMarketplaceProduct?: (categoryId: number, productId: number) => void;
  focusPlatformId?: number | null;
  onFocusPlatformConsumed?: () => void;
}

type DrawerStep = 'project' | WorkspaceDrawerStep;
type WorkspaceTab = 'innovation-platform' | 'jobs-to-be-done';

const DRAWER_MIN_WIDTH_PX = 320;
const DRAWER_NARROW_DEFAULT_PX = 448;

function getDrawerMaxWidthPx() {
  if (typeof window === 'undefined') return 896;
  return Math.round(window.innerWidth * 0.9);
}

function clampDrawerWidthPx(width: number) {
  return Math.min(getDrawerMaxWidthPx(), Math.max(DRAWER_MIN_WIDTH_PX, Math.round(width)));
}

function getJobsTabDrawerWidthPx() {
  if (typeof window === 'undefined') return 896;
  return clampDrawerWidthPx(Math.round(window.innerWidth * 0.6));
}

const inputClass =
  'w-full px-4 py-2.5 border border-[#E0DEE8] rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#43C8F5]/40 focus:border-[#43C8F5]';

const textareaClass = `${inputClass} resize-y min-h-[100px]`;

function summarizeText(text: string, max = 48): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trim()}…`;
}

function platformDisplayLabel(platform: InnovationPlatform): string {
  const job = platform.job_to_be_done?.trim() || '';
  const tension = platform.tension?.trim() || '';
  if (job && tension) {
    const jobLabel = summarizeText(job.replace(/^when\s+/i, ''), 36);
    const tensionLabel = summarizeText(tension.replace(/^but,?\s*/i, ''), 52);
    return `${jobLabel} / ${tensionLabel}`;
  }
  return tension || job || 'Untitled innovation platform';
}

async function fetchPlatformConceptLinks(platformId: number): Promise<PlatformConceptLink[]> {
  try {
    const publishedRes = await fetch(`/api/blueprints/${platformId}/published-blueprints/`, {
      credentials: 'include',
    });
    if (publishedRes.ok) {
      const published = await publishedRes.json();
      if (Array.isArray(published) && published.length > 0) {
        return published.map((row: {
          combinationId?: number;
          combination_id?: number;
          artifactId?: number;
          artifact_id?: number;
          label?: string;
          recipe_card_name?: string;
          combination_name?: string;
          concept_name?: string;
        }) => {
          const combinationId = row.combinationId ?? row.combination_id ?? 0;
          const artifactId = row.artifactId ?? row.artifact_id ?? 0;
          const label =
            row.label?.trim() ||
            [row.recipe_card_name, row.combination_name, row.concept_name]
              .filter(Boolean)
              .join(' / ') ||
            'Concept';
          return { combinationId, artifactId, label };
        });
      }
    }

    // Fallback for older backends / pre-migration data without published rows.
    const response = await fetch(`/api/blueprints/${platformId}/recipe-combinations/`, {
      credentials: 'include',
    });
    if (!response.ok) return [];
    const data = await response.json();
    const combos = Array.isArray(data) ? data : [];
    const links: PlatformConceptLink[] = [];

    combos.forEach((combo: {
      id: number;
      tab_name?: string;
      combination_name?: string;
      artifacts?: Array<{ id: number; name?: string; visuals?: unknown[]; visual_concept?: string }>;
    }, comboIndex: number) => {
      const recipeCardTabName = combo.tab_name?.trim() || 'Recipe Card';
      const combinationName = combo.combination_name?.trim() || `Combination ${comboIndex + 1}`;
      const artifacts = Array.isArray(combo.artifacts) ? combo.artifacts : [];
      artifacts.forEach((artifact, index) => {
        const hasVisual =
          Boolean((artifact.visual_concept || '').trim()) ||
          (Array.isArray(artifact.visuals) && artifact.visuals.length > 0);
        if (!hasVisual) return;
        const conceptName = artifact.name?.trim() || `Concept ${index + 1}`;
        const label = [recipeCardTabName, combinationName, conceptName].join(' / ');
        links.push({
          combinationId: combo.id,
          artifactId: artifact.id,
          label,
        });
      });
    });

    return links;
  } catch {
    return [];
  }
}

function findJobIdForPlatform(platformId: number, items: JobItem[]): string {
  const match = items.find((item) => item.platformId === platformId);
  if (match) return match.id;
  return `user-${platformId}`;
}

function hasInnovationPlatformEntries(platformList: InnovationPlatform[]): boolean {
  return platformList.some(
    (platform) => Boolean(platform.job_to_be_done?.trim() && platform.tension?.trim())
  );
}

const workspaceLinkClass =
  'text-sm font-medium text-[#43C8F5] underline hover:text-[#2A9CC4] transition-colors';

const workspaceLinkUnboldClass =
  'text-sm text-[#43C8F5] underline hover:text-[#2A9CC4] transition-colors';

const workspacePurplePillClass =
  'px-4 py-1.5 rounded-full bg-[#655FA8] text-white text-xs font-semibold hover:bg-[#544fa0] transition-colors whitespace-nowrap';

const workspacePurpleExploreClass =
  'shrink-0 px-6 py-2.5 rounded-full bg-[#655FA8] text-white text-sm font-semibold hover:bg-[#544fa0] transition-colors whitespace-nowrap';

type JobsIdentifierServiceId = 'catalyx-ai' | 'catalyx-crowd';

const JOBS_IDENTIFIER_SERVICES: Array<{
  id: JobsIdentifierServiceId;
  title: string;
  description: string;
  pricing: string;
  credits: number;
}> = [
  {
    id: 'catalyx-ai',
    title: 'Catalyx AI',
    description:
      'Catalyx AI generates 15 Jobs To Be Done, scanning and synthesising all online information.',
    pricing: 'Price from 400 credits in 3 days',
    credits: 400,
  },
  {
    id: 'catalyx-crowd',
    title: 'Catalyx Crowd Jobs Identifier',
    description:
      '200 consumers talk us through their needs, desires, hopes and expectations to generate 15 Jobs To Be Done.',
    pricing: 'Price from 600 credits in 6 days',
    credits: 600,
  },
];

const JOBS_PRIORITISER_SERVICES = [
  {
    id: 'catalyx-crowd',
    title: 'Catalyx Crowd Jobs Prioritiser',
    description:
      'Prioritise Jobs with 500 consumers—assessing relevance, urgency, unmet needs, and willingness to pay.',
    pricing: 'Price from 900 credits in 10 days',
  },
] as const;

const PLATFORM_PRIORITISER_SERVICES = [
  {
    id: 'catalyx-crowd',
    title: 'Catalyx Crowd Platform Prioritiser',
    description:
      'Validate and prioritise Innovation Platforms with 200 consumers, ensuring strongest opportunities and appropriate brand fit.',
    pricing: 'Price from 50 credits in 4 days',
  },
] as const;

const PLATFORM_PRIORITISER_MAX_TENSIONS = 15;

interface PrioritiserTensionSelection {
  key: string;
  jobId: string;
  jobText: string;
  tensionId: string;
  tensionText: string;
  market: string;
  blueprintId: number | null;
}

type TensionIdentifierServiceId = 'catalyx-ai' | 'catalyx-crowd';

const TENSION_IDENTIFIER_SERVICES: Array<{
  id: TensionIdentifierServiceId;
  title: string;
  description: string;
  pricing: string;
  credits: number;
}> = [
  {
    id: 'catalyx-ai',
    title: 'Catalyx AI Tension Identifier',
    description: 'Use Catalyx AI to quickly identify Tensions linked to each Job from online.',
    pricing: 'Price from 30 credits in 3 days',
    credits: 30,
  },
  {
    id: 'catalyx-crowd',
    title: 'Catalyx Crowd Tension Identifier',
    description:
      'Observe 50 consumers tackling Jobs to reveal tensions, gaps, and real behaviours through rich contextual insight.',
    pricing: 'Price from 60 credits in 7 days',
    credits: 60,
  },
];

const workspaceOutlinedButtonClass =
  'inline-flex items-center gap-2 rounded-xl border border-[#43C8F5] bg-white px-4 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors shrink-0';

function formatJobDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function emailToUsername(email?: string): string {
  if (!email) return '—';
  return email.split('@')[0] || email;
}

function withJobStartingPosition(
  part: string,
  prefix: string,
  alreadyHasPrefix: RegExp,
): string {
  const trimmed = part.trim().replace(/^,+/, '').replace(/,+$/, '').trim();
  if (!trimmed) return '';
  if (alreadyHasPrefix.test(trimmed)) {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }
  return `${prefix} ${trimmed}`;
}

function buildJobToBeDoneText(whenPart: string, wantPart: string, soCanPart: string): string {
  const when = withJobStartingPosition(
    whenPart,
    "When I'm",
    /^when\s+i(?:'m|’m| am)?\b/i,
  );
  const want = withJobStartingPosition(wantPart, 'I want', /^i want\b/i);
  const soThat = withJobStartingPosition(
    soCanPart,
    'So that',
    /^so(?:\s+that)?\b/i,
  );
  return [when, want, soThat].filter(Boolean).join(', ');
}

function splitJobToBeDone(text: string): { when: string; want: string; soThat: string } {
  const t = (text || '').trim();
  const wantMatch = t.match(/\bI want\b/i);
  if (!wantMatch || wantMatch.index === undefined) return { when: t, want: '', soThat: '' };
  const when = t.slice(0, wantMatch.index).replace(/[,\s]+$/, '').trim();
  const rest = t.slice(wantMatch.index).trim();
  const soMatch = rest.match(/\bso(?:\s+that)?\b/i);
  if (soMatch && soMatch.index !== undefined && soMatch.index > 0) {
    return {
      when,
      want: rest.slice(0, soMatch.index).replace(/[,\s]+$/, '').trim(),
      soThat: rest.slice(soMatch.index).trim(),
    };
  }
  return { when, want: rest, soThat: '' };
}

function jobHideSignature(market: string, text: string): string {
  return `${(market || '').trim().toLowerCase()}|${text.trim().toLowerCase()}`;
}

function tensionHideSignature(market: string, jobText: string, tensionText: string): string {
  return `${(market || '').trim().toLowerCase()}|${jobText.trim().toLowerCase()}|${tensionText.trim().toLowerCase()}`;
}

function filterTensionsForHidden(
  tensions: JobTensionItem[],
  jobText: string,
  market: string,
  hiddenTensionSigs: Set<string>
): JobTensionItem[] {
  return tensions.filter(
    (tension) => !hiddenTensionSigs.has(tensionHideSignature(market, jobText, tension.text))
  );
}

function normalizeScore(score: unknown): number {
  const value = typeof score === 'number' ? score : Number(score);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(15, Math.round(value)));
}

/** 0 = gray; 1–3 green; 4–12 orange; 13–15 red */
function scoreDotClass(score: number): string {
  if (score <= 0) return 'bg-gray-300 text-gray-700';
  if (score <= 3) return 'bg-emerald-500 text-white';
  if (score <= 12) return 'bg-orange-400 text-white';
  return 'bg-red-500 text-white';
}

function ScoreDot({ score }: { score: number }) {
  const normalized = normalizeScore(score);
  return (
    <span
      className={`shrink-0 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center ${scoreDotClass(normalized)}`}
    >
      {normalized}
    </span>
  );
}

function jobTextsMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function platformMatchesJobTension(
  platform: InnovationPlatform,
  jobText: string,
  tensionText: string,
  market?: string
): boolean {
  const platformTension = (platform.tension || '').trim().toLowerCase();
  const normalizedTension = tensionText.trim().toLowerCase();
  if (!platformTension || !normalizedTension) return false;
  const normalizedMarket = (market || '').trim().toLowerCase();
  const platformMarket = (platform.market || '').trim().toLowerCase();
  const marketMatches =
    !normalizedMarket || !platformMarket || platformMarket === normalizedMarket;
  return (
    marketMatches &&
    jobTextsMatch(platform.job_to_be_done || '', jobText) &&
    platformTension === normalizedTension
  );
}

function markTensionsLinkedToPlatforms(items: JobItem[], platformList: InnovationPlatform[]) {
  for (const item of items) {
    item.tensions = item.tensions.map((tension) => ({
      ...tension,
      platformLinked: platformList.some((platform) =>
        platformMatchesJobTension(platform, item.text, tension.text, item.market)
      ),
    }));
  }
}

function isInnovationPlatformBlueprint(platform: InnovationPlatform): boolean {
  return Boolean(platform.job_to_be_done?.trim() && platform.tension?.trim());
}

function pickBlueprintForTensionLookup(
  platformList: InnovationPlatform[],
  market: string,
  fallback?: InnovationPlatform
): InnovationPlatform | undefined {
  const normalizedMarket = market.trim();
  return (
    platformList.find(
      (platform) =>
        !isInnovationPlatformBlueprint(platform) &&
        Boolean(platform.job_to_be_done?.trim()) &&
        (!normalizedMarket || (platform.market || '').trim() === normalizedMarket)
    ) || fallback
  );
}

async function fetchTensionsForJob(
  projectId: number,
  jobText: string,
  market: string,
  platformList: InnovationPlatform[],
  fallbackPlatform?: InnovationPlatform
): Promise<JobTensionItem[]> {
  const tensions: JobTensionItem[] = [];
  const normalizedJob = jobText.trim();
  if (!normalizedJob) return tensions;

  const tensionBlueprint = pickBlueprintForTensionLookup(
    platformList,
    market,
    fallbackPlatform
  );

  if (tensionBlueprint) {
    try {
      const response = await fetch(
        `/api/blueprints/${tensionBlueprint.id}/tensions-latest/?jtbd=${encodeURIComponent(normalizedJob)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data.tensions) ? data.tensions : [];
        list.forEach((item: { tension_text?: string; metadata?: Record<string, unknown> }, idx: number) => {
          const text = (item.tension_text || '').trim();
          if (!text) return;
          const meta = item.metadata || {};
          tensions.push({
            id: `ai-t-${tensionBlueprint.id}-${normalizedJob.length}-${idx}`,
            text,
            source: 'catalyx-ai',
            score: 0,
            deepDive:
              (typeof meta.tension_deep_dive === 'string' && meta.tension_deep_dive) ||
              (typeof meta.deep_dive === 'string' && meta.deep_dive) ||
              undefined,
          });
        });
      }
    } catch {
      // Optional AI tensions.
    }
  }

  try {
    const params = new URLSearchParams({
      job_to_be_done: normalizedJob,
    });
    const normalizedMarket = market.trim();
    if (normalizedMarket) {
      params.set('market', normalizedMarket);
    }
    const userResponse = await fetch(
      `/api/projects/${projectId}/job-tensions/?${params.toString()}`,
      { credentials: 'include' }
    );
    if (userResponse.ok) {
      const userTensions = await userResponse.json();
      const list = Array.isArray(userTensions) ? userTensions : [];
      list.forEach((item: { id: number; description?: string; has_been_evidenced?: boolean; score?: number }) => {
        const text = (item.description || '').trim();
        if (!text) return;
        const duplicate = tensions.find((t) => t.text.toLowerCase() === text.toLowerCase());
        if (duplicate) return;
        tensions.push({
          id: `pjt-${item.id}`,
          userTensionId: item.id,
          text,
          source: 'user',
          score: normalizeScore(item.score),
          evidenced: Boolean(item.has_been_evidenced),
        });
      });
    }
  } catch {
    // Optional user tensions.
  }

  return tensions;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onProjectCreated,
  onProjectContinue,
  onWorkspaceDataChange,
  mode = 'create',
  existingProjectId = null,
  bootstrapProject = null,
  initialDrawerStep = 'platform-form',
  highlightedJobId = null,
  highlightedJobText = null,
  workspaceTabRequest = null,
  onWorkspaceTabRequestConsumed,
  onViewWorkspace,
  onViewJob,
  onViewConcept,
  onNavigateToMarketplaceProduct,
  focusPlatformId = null,
  onFocusPlatformConsumed,
}) => {
  const { can } = useCaps();
  const canCreateProject = can('projects_create');
  const isWorkspaceMode = mode === 'workspace';
  const [drawerStep, setDrawerStep] = useState<DrawerStep>('project');
  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>('innovation-platform');
  const [createdProject, setCreatedProject] = useState<ProjectCreatedPayload | null>(null);
  const [platforms, setPlatforms] = useState<InnovationPlatform[]>([]);
  const [platformConceptLinks, setPlatformConceptLinks] = useState<Record<number, PlatformConceptLink[]>>({});
  const [expandedPlatformId, setExpandedPlatformId] = useState<number | null>(null);
  const [jobItems, setJobItems] = useState<JobItem[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [workspaceProducts, setWorkspaceProducts] = useState<Partial<Record<WorkspaceKey, MarketplaceProduct[]>>>({});
  const [workspaceProductsLoading, setWorkspaceProductsLoading] = useState(false);
  const [selectedTensionByJobId, setSelectedTensionByJobId] = useState<Record<string, string>>({});
  const [createInnovationPlatformError, setCreateInnovationPlatformError] = useState<string | null>(null);
  const [createInnovationPlatformJobId, setCreateInnovationPlatformJobId] = useState<string | null>(null);
  const [creatingPlatformJobId, setCreatingPlatformJobId] = useState<string | null>(null);
  const [expandedTensionDeepDiveId, setExpandedTensionDeepDiveId] = useState<string | null>(null);
  const [openTensionMenuId, setOpenTensionMenuId] = useState<string | null>(null);
  const [openJobMenuId, setOpenJobMenuId] = useState<string | null>(null);
  const [addTensionForJob, setAddTensionForJob] = useState<JobItem | null>(null);
  const [newTensionDescription, setNewTensionDescription] = useState('');
  const [newTensionEvidenced, setNewTensionEvidenced] = useState(false);
  const [addTensionError, setAddTensionError] = useState<string | null>(null);
  const [drawerWidthPx, setDrawerWidthPx] = useState<number | null>(null);
  const drawerResizeStartRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showJobsIdentifierModal, setShowJobsIdentifierModal] = useState(false);
  const [showJobsPrioritiserModal, setShowJobsPrioritiserModal] = useState(false);
  const [showPlatformPrioritiserModal, setShowPlatformPrioritiserModal] = useState(false);
  const [prioritiserSelectMode, setPrioritiserSelectMode] = useState(false);
  const [prioritiserSelections, setPrioritiserSelections] = useState<Record<string, PrioritiserTensionSelection>>({});
  const [showPlatformPrioritiserConfirm, setShowPlatformPrioritiserConfirm] = useState(false);
  const [prioritiserSaving, setPrioritiserSaving] = useState(false);
  const [prioritiserError, setPrioritiserError] = useState<string | null>(null);
  const [prioritiserSuccessNotice, setPrioritiserSuccessNotice] = useState<string | null>(null);
  const [showTensionIdentifierModal, setShowTensionIdentifierModal] = useState(false);
  const [tensionIdentifierJob, setTensionIdentifierJob] = useState<JobItem | null>(null);
  const [tensionIdentifierPendingService, setTensionIdentifierPendingService] =
    useState<TensionIdentifierServiceId | null>(null);
  const [tensionIdentifierBusy, setTensionIdentifierBusy] = useState(false);
  const [tensionIdentifierError, setTensionIdentifierError] = useState<string | null>(null);
  const [jobsIdentifierMarket, setJobsIdentifierMarket] = useState('');
  const [jobsIdentifierPendingService, setJobsIdentifierPendingService] = useState<JobsIdentifierServiceId | null>(null);
  const [jobsIdentifierBusy, setJobsIdentifierBusy] = useState(false);
  const [jobsIdentifierError, setJobsIdentifierError] = useState<string | null>(null);
  const [jobWhenPart, setJobWhenPart] = useState('');
  const [jobWantPart, setJobWantPart] = useState('');
  const [jobSoCanPart, setJobSoCanPart] = useState('');
  const [jobMarket, setJobMarket] = useState('');
  const [jobHasBeenEvidenced, setJobHasBeenEvidenced] = useState(false);
  const [addJobError, setAddJobError] = useState<string | null>(null);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [editingTensionId, setEditingTensionId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    kind: 'job' | 'tension';
    job: JobItem;
    tension?: JobTensionItem;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Step 1 — project details
  const [name, setName] = useState('');
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [marketQuery, setMarketQuery] = useState('');
  const [marketResults, setMarketResults] = useState<Country[]>([]);
  const [showMarketDropdown, setShowMarketDropdown] = useState(false);
  const [isSearchingMarkets, setIsSearchingMarkets] = useState(false);
  const [opportunitySpace, setOpportunitySpace] = useState('');
  const [gatherProjectInfo, setGatherProjectInfo] = useState(false);
  const [showOpportunityHelpModal, setShowOpportunityHelpModal] = useState(false);

  // Step 2 — innovation platform form
  const [jobToBeDone, setJobToBeDone] = useState('');
  const [tension, setTension] = useState('');
  const [platformMarket, setPlatformMarket] = useState('');
  const [hasBeenEvidenced, setHasBeenEvidenced] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const marketContainerRef = useRef<HTMLDivElement>(null);
  const marketSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightedJobRef = useRef<HTMLDivElement | null>(null);
  const platformCardRef = useRef<HTMLDivElement | null>(null);
  const jobsListScrollRef = useRef<HTMLDivElement | null>(null);
  const innovationTabWidthRef = useRef<number | null>(null);
  const workspaceLoadGenRef = useRef(0);
  const prevWorkspaceTabRef = useRef<WorkspaceTab>('innovation-platform');
  const [pendingPlatformScrollId, setPendingPlatformScrollId] = useState<number | null>(null);
  const [jobMenuAnchor, setJobMenuAnchor] = useState<{ top: number; right: number } | null>(null);
  const [tensionMenuAnchor, setTensionMenuAnchor] = useState<{ top: number; right: number } | null>(null);

  const loadPlatformConceptLinks = useCallback(async (platformList: InnovationPlatform[]) => {
    const entries = await Promise.all(
      platformList.map(async (platform) => [platform.id, await fetchPlatformConceptLinks(platform.id)] as const)
    );
    setPlatformConceptLinks(Object.fromEntries(entries));
  }, []);

  const fetchPlatforms = useCallback(async (projectId: number) => {
    try {
      const response = await fetch(
        `/api/projects/${projectId}/blueprints/?uses_new_workflow=true`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setPlatforms(list);
        void loadPlatformConceptLinks(list);
        return list as InnovationPlatform[];
      }
    } catch (err) {
      console.error('Error fetching innovation platforms:', err);
    }
    return [];
  }, [loadPlatformConceptLinks]);

  const innovationPlatformEntries = useMemo(
    () => platforms.filter(isInnovationPlatformBlueprint),
    [platforms]
  );

  const fetchCountries = useCallback(async (query: string) => {
    setIsSearchingMarkets(true);
    try {
      const params = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
      const response = await fetch(`/api/countries/${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMarketResults(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
    } finally {
      setIsSearchingMarkets(false);
    }
  }, []);

  const buildJobItems = useCallback(async (projectId: number, platformList: InnovationPlatform[]) => {
    const items: JobItem[] = [];
    const seenKeys = new Set<string>();

    const hiddenJobSigs = new Set<string>();
    const hiddenTensionSigs = new Set<string>();
    try {
      const hiddenRes = await fetch(`/api/projects/${projectId}/hidden-workspace-items/`, {
        credentials: 'include',
      });
      if (hiddenRes.ok) {
        const hiddenItems = await hiddenRes.json();
        const list = Array.isArray(hiddenItems) ? hiddenItems : [];
        list.forEach((item: { kind?: string; job_to_be_done?: string; market?: string; tension_text?: string }) => {
          if (item.kind === 'job') {
            hiddenJobSigs.add(jobHideSignature(item.market || '', item.job_to_be_done || ''));
          } else if (item.kind === 'tension') {
            hiddenTensionSigs.add(
              tensionHideSignature(
                item.market || '',
                item.job_to_be_done || '',
                item.tension_text || ''
              )
            );
          }
        });
      }
    } catch {
      // Hidden items are optional; list still loads without them.
    }

    const platformById = new Map(platformList.map((p) => [p.id, p]));

    const jobDedupKey = (text: string, market: string) =>
      `${text.trim().toLowerCase()}|${(market || '').trim().toLowerCase()}`;

    for (const platform of platformList) {
      if (isInnovationPlatformBlueprint(platform)) continue;
      try {
        const response = await fetch(`/api/blueprints/${platform.id}/jtbd-latest/`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          const statements = Array.isArray(data.jtbd_statements) ? data.jtbd_statements : [];
          for (const [statementIndex, statement] of statements.entries()) {
            const text = typeof statement === 'string' ? statement.trim() : '';
            const market = platform.market || '';
            const key = jobDedupKey(text, market);
            if (!text || seenKeys.has(key)) continue;
            if (hiddenJobSigs.has(jobHideSignature(market, text))) continue;
            seenKeys.add(key);
            const tensions = filterTensionsForHidden(
              await fetchTensionsForJob(
                projectId,
                text,
                platform.market || '',
                platformList,
                platform
              ),
              text,
              market,
              hiddenTensionSigs
            );
            items.push({
              id: `ai-${platform.id}-${statementIndex}`,
              text,
              source: 'catalyx-ai',
              evidenced: true,
              score: normalizeScore(platform.job_score),
              platformId: platform.id,
              market: platform.market,
              addedBy: emailToUsername(platform.created_by_email),
              createdOn: platform.created_at,
              tensions,
              tensionCount: tensions.length,
            });
          }
        }
      } catch {
        // AI jobs are optional; user-added jobs still show below.
      }
    }

    for (const platform of platformList) {
      if (isInnovationPlatformBlueprint(platform)) continue;
      const text = platform.job_to_be_done?.trim() || '';
      const market = platform.market || '';
      const key = jobDedupKey(text, market);
      if (!text || seenKeys.has(key)) continue;
      if (hiddenJobSigs.has(jobHideSignature(market, text))) continue;
      seenKeys.add(key);
      const tensions = filterTensionsForHidden(
        await fetchTensionsForJob(
          projectId,
          text,
          market,
          platformList,
          platform
        ),
        text,
        market,
        hiddenTensionSigs
      );
      items.push({
        id: `user-${platform.id}`,
        text,
        source: 'user',
        evidenced: Boolean(platform.has_been_evidenced),
        score: normalizeScore(platform.job_score),
        platformId: platform.id,
        market: platform.market,
        addedBy: emailToUsername(platform.created_by_email),
        createdOn: platform.created_at,
        tensions,
        tensionCount: tensions.length,
      });
    }

    for (const platform of platformList) {
      if (!isInnovationPlatformBlueprint(platform)) continue;
      const text = platform.job_to_be_done?.trim() || '';
      const market = platform.market || '';
      const key = jobDedupKey(text, market);
      if (!text || seenKeys.has(key)) continue;
      if (hiddenJobSigs.has(jobHideSignature(market, text))) continue;
      seenKeys.add(key);
      let tensions = filterTensionsForHidden(
        await fetchTensionsForJob(
          projectId,
          text,
          platform.market || '',
          platformList,
          platform
        ),
        text,
        market,
        hiddenTensionSigs
      );
      const tensionText = (platform.tension || '').trim();
      if (tensionText) {
        const hasTension = tensions.some(
          (tension) => tension.text.trim().toLowerCase() === tensionText.toLowerCase()
        );
        if (!hasTension && !hiddenTensionSigs.has(tensionHideSignature(market, text, tensionText))) {
          tensions.unshift({
            id: `platform-tension-${platform.id}`,
            text: tensionText,
            source: 'user',
            score: normalizeScore(platform.tension_score),
            evidenced: Boolean(platform.has_been_evidenced),
            platformLinked: true,
          });
        }
      }
      items.push({
        id: `user-${platform.id}`,
        text,
        source: 'user',
        evidenced: Boolean(platform.has_been_evidenced),
        score: normalizeScore(platform.job_score),
        platformId: platform.id,
        market: platform.market,
        addedBy: emailToUsername(platform.created_by_email),
        createdOn: platform.created_at,
        tensions,
        tensionCount: tensions.length,
      });
    }

    // Ensure platform metadata is attached even when only AI duplicate paths were skipped
    for (const item of items) {
      if (!item.platformId) continue;
      const platform = platformById.get(item.platformId);
      if (!platform) continue;
      if (!item.addedBy || item.addedBy === '—') item.addedBy = emailToUsername(platform.created_by_email);
      if (!item.createdOn) item.createdOn = platform.created_at;
      if (!item.market) item.market = platform.market;
    }

    markTensionsLinkedToPlatforms(items, platformList);

    setJobItems(items);
    return items;
  }, []);

  const loadJobsTab = useCallback(async () => {
    if (!createdProject?.id) return;
    setJobsLoading(true);
    try {
      const list = await fetchPlatforms(createdProject.id);
      await buildJobItems(createdProject.id, list);
    } finally {
      setJobsLoading(false);
    }
  }, [createdProject?.id, fetchPlatforms, buildJobItems]);

  const notifyWorkspaceDataChange = useCallback(() => {
    if (isWorkspaceMode) {
      onWorkspaceDataChange?.();
    }
  }, [isWorkspaceMode, onWorkspaceDataChange]);

  const handleViewWorkspaceClick = (platform: InnovationPlatform) => {
    onViewWorkspace?.(platform.id);
  };

  const handleViewJobClick = async (platform: InnovationPlatform) => {
    if (!createdProject?.id) return;
    let items = jobItems;
    if (!items.some((item) => item.platformId === platform.id)) {
      const list = await fetchPlatforms(createdProject.id);
      items = await buildJobItems(createdProject.id, list);
    }
    const jobId = findJobIdForPlatform(platform.id, items);
    if (onViewJob) {
      onViewJob(jobId);
      return;
    }
    setWorkspaceTab('jobs-to-be-done');
    setDrawerStep('platform-list');
    setExpandedJobId(jobId);
  };

  const handleViewConceptClick = (platform: InnovationPlatform, link: PlatformConceptLink) => {
    onViewConcept?.(platform.id, link.combinationId, link.artifactId);
  };

  useEffect(() => {
    if (!isOpen || !workspaceTabRequest) return;
    setWorkspaceTab(workspaceTabRequest);
    setDrawerStep('platform-list');
    onWorkspaceTabRequestConsumed?.();
  }, [isOpen, workspaceTabRequest, onWorkspaceTabRequestConsumed]);

  useEffect(() => {
    if (!highlightedJobId || workspaceTab !== 'jobs-to-be-done') return;
    setExpandedJobId(highlightedJobId);
    const timer = window.setTimeout(() => {
      highlightedJobRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [highlightedJobId, workspaceTab, jobItems.length]);

  useEffect(() => {
    if (!highlightedJobText || workspaceTab !== 'jobs-to-be-done' || jobItems.length === 0) return;
    const normalized = highlightedJobText.trim().toLowerCase();
    const match = jobItems.find((job) => job.text.trim().toLowerCase() === normalized);
    if (!match) return;
    setExpandedJobId(match.id);
    const timer = window.setTimeout(() => {
      highlightedJobRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [highlightedJobText, workspaceTab, jobItems]);

  useEffect(() => {
    if (!isOpen || !isWorkspaceMode) return;
    let cancelled = false;
    setWorkspaceProductsLoading(true);
    const keys: WorkspaceKey[] = [
      'jobs_identifier',
      'jobs_prioritiser',
      'platform_prioritiser',
      'tension_identifier',
    ];
    Promise.all(
      keys.map(async (key) => [key, await queryClient.ensureQueryData(marketplaceProductsQuery(key))] as const)
    )
      .then((entries) => {
        if (!cancelled) {
          setWorkspaceProducts(Object.fromEntries(entries));
        }
      })
      .finally(() => {
        if (!cancelled) setWorkspaceProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isWorkspaceMode]);

  useEffect(() => {
    if (!isOpen || !focusPlatformId) return;
    setWorkspaceTab('innovation-platform');
    setDrawerStep('platform-list');
    setExpandedPlatformId(focusPlatformId);
    setPendingPlatformScrollId(focusPlatformId);
    onFocusPlatformConsumed?.();
  }, [isOpen, focusPlatformId, onFocusPlatformConsumed]);

  useEffect(() => {
    if (!pendingPlatformScrollId || expandedPlatformId !== pendingPlatformScrollId) return;
    const timer = window.setTimeout(() => {
      if (platformCardRef.current) {
        platformCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setPendingPlatformScrollId(null);
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, [pendingPlatformScrollId, expandedPlatformId, workspaceTab, platforms]);

  useEffect(() => {
    const prevTab = prevWorkspaceTabRef.current;
    if (prevTab === workspaceTab) return;
    prevWorkspaceTabRef.current = workspaceTab;

    if (workspaceTab === 'jobs-to-be-done') {
      setDrawerWidthPx((current) => {
        const currentWidth = current ?? DRAWER_NARROW_DEFAULT_PX;
        innovationTabWidthRef.current = currentWidth;
        return getJobsTabDrawerWidthPx();
      });
      return;
    }

    if (workspaceTab === 'innovation-platform' && prevTab === 'jobs-to-be-done') {
      const restore = innovationTabWidthRef.current;
      innovationTabWidthRef.current = null;
      setDrawerWidthPx(restore);
    }
  }, [workspaceTab]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!isWorkspaceMode) {
      return;
    }

    let cancelled = false;
    const loadGen = ++workspaceLoadGenRef.current;

    const pendingTabRequest = workspaceTabRequest;
    const pendingFocusPlatformId = focusPlatformId;

    const loadWorkspace = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const projectId = bootstrapProject?.id ?? existingProjectId;
        if (!projectId) return;

        // Always fetch the project so markets (and other fields) are available.
        // Notification / bootstrap payloads often only include id + name.
        const response = await fetch(`/api/projects/${projectId}/`, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to load project');
        const fullProject = await response.json();
        if (cancelled) return;

        const bootstrapMarkets = bootstrapProject?.markets;
        const markets = Array.isArray(fullProject.markets)
          ? fullProject.markets
          : Array.isArray(bootstrapMarkets)
            ? bootstrapMarkets
            : [];
        const project: ProjectCreatedPayload = {
          ...fullProject,
          id: fullProject.id ?? projectId,
          name: bootstrapProject?.name || fullProject.name,
          markets,
        };

        setCreatedProject(project);
        setSelectedMarkets(markets);
        setPlatformMarket(markets[0] || '');
        if (bootstrapProject) {
          setWorkspaceTab('innovation-platform');
        } else if (
          pendingTabRequest === 'innovation-platform' ||
          pendingFocusPlatformId != null
        ) {
          setWorkspaceTab('innovation-platform');
        } else {
          setWorkspaceTab('jobs-to-be-done');
        }

        const list = await fetchPlatforms(project.id);
        if (cancelled) return;
        setDrawerStep(hasInnovationPlatformEntries(list) ? 'platform-list' : 'platform-form');
        await buildJobItems(project.id, list);
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load project';
          setError(message);
        }
      } finally {
        if (workspaceLoadGenRef.current === loadGen) {
          setIsLoading(false);
        }
      }
    };

    if (!bootstrapProject && !existingProjectId) {
      if (workspaceLoadGenRef.current === loadGen) {
        setIsLoading(false);
      }
      return;
    }

    void loadWorkspace();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    isWorkspaceMode,
    existingProjectId,
    bootstrapProject,
    initialDrawerStep,
    fetchPlatforms,
    buildJobItems,
    workspaceTabRequest,
    focusPlatformId,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setFieldErrors({});
    fetchCountries('');
  }, [isOpen, fetchCountries]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (marketContainerRef.current && !marketContainerRef.current.contains(event.target as Node)) {
        setShowMarketDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!showMarketDropdown) return;
    if (marketSearchTimeoutRef.current) clearTimeout(marketSearchTimeoutRef.current);
    marketSearchTimeoutRef.current = setTimeout(() => {
      fetchCountries(marketQuery);
    }, 200);
    return () => {
      if (marketSearchTimeoutRef.current) clearTimeout(marketSearchTimeoutRef.current);
    };
  }, [marketQuery, showMarketDropdown, fetchCountries]);

  useEffect(() => {
    if (!isOpen || workspaceTab !== 'jobs-to-be-done' || !createdProject?.id) return;
    void loadJobsTab();
  }, [isOpen, workspaceTab, createdProject?.id, loadJobsTab]);

  useEffect(() => {
    if (!openJobMenuId && !openTensionMenuId) return;
    const closeMenus = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-job-menu]')) return;
      setOpenJobMenuId(null);
      setOpenTensionMenuId(null);
      setJobMenuAnchor(null);
      setTensionMenuAnchor(null);
    };
    document.addEventListener('mousedown', closeMenus);
    return () => document.removeEventListener('mousedown', closeMenus);
  }, [openJobMenuId, openTensionMenuId]);

  const closeActionMenus = () => {
    setOpenJobMenuId(null);
    setOpenTensionMenuId(null);
    setJobMenuAnchor(null);
    setTensionMenuAnchor(null);
  };

  const openJobActionMenu = (jobId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    if (openJobMenuId === jobId) {
      closeActionMenus();
      return;
    }
    setOpenTensionMenuId(null);
    setTensionMenuAnchor(null);
    setOpenJobMenuId(jobId);
    setJobMenuAnchor({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  const openTensionActionMenu = (tensionId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    if (openTensionMenuId === tensionId) {
      closeActionMenus();
      return;
    }
    setOpenJobMenuId(null);
    setJobMenuAnchor(null);
    setOpenTensionMenuId(tensionId);
    setTensionMenuAnchor({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  const handleJobsListScroll = () => {
    if (openJobMenuId || openTensionMenuId) {
      closeActionMenus();
    }
  };

  const getDefaultDrawerWidthPx = useCallback(() => DRAWER_NARROW_DEFAULT_PX, []);

  const effectiveDrawerWidthPx = clampDrawerWidthPx(drawerWidthPx ?? getDefaultDrawerWidthPx());

  useEffect(() => {
    if (!isOpen) return;
    const onWindowResize = () => {
      setDrawerWidthPx((current) => {
        if (current == null) return current;
        return clampDrawerWidthPx(current);
      });
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, [isOpen]);

  const handleDrawerResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      drawerResizeStartRef.current = { startX: event.clientX, startWidth: effectiveDrawerWidthPx };

      const onMouseMove = (moveEvent: MouseEvent) => {
        const start = drawerResizeStartRef.current;
        if (!start) return;
        const next = clampDrawerWidthPx(start.startWidth + (moveEvent.clientX - start.startX));
        setDrawerWidthPx(next);
      };

      const onMouseUp = () => {
        drawerResizeStartRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [effectiveDrawerWidthPx]
  );

  const renderDrawerResizeHandle = (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize drawer"
      className="absolute top-0 right-0 z-30 h-full w-3 cursor-col-resize touch-none"
      onMouseDown={handleDrawerResizeStart}
    >
      <div className="absolute inset-y-0 right-0 w-1 bg-[#E0DEE8]/80 hover:bg-[#43C8F5]/50 active:bg-[#43C8F5]/70 transition-colors" />
      <GripVertical className="pointer-events-none absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 w-3.5 h-3.5 text-gray-400" />
    </div>
  );

  const renderDrawerShell = (children: React.ReactNode, options?: { center?: boolean }) => (
    <aside
      style={{ width: effectiveDrawerWidthPx }}
      className={`relative h-full shrink-0 bg-white shadow-2xl flex flex-col animate-in slide-in-from-left duration-300 overflow-hidden pointer-events-auto border-l-2 border-[#43C8F5] ${
        options?.center ? 'items-center justify-center' : ''
      }`}
      role="dialog"
      aria-modal
      aria-labelledby="create-project-title"
    >
      {children}
      {renderDrawerResizeHandle}
    </aside>
  );

  const resetAll = () => {
    setDrawerStep('project');
    setWorkspaceTab('innovation-platform');
    setCreatedProject(null);
    setPlatforms([]);
    setPlatformConceptLinks({});
    setExpandedPlatformId(null);
    setJobItems([]);
    setJobsLoading(false);
    setExpandedJobId(null);
    setSelectedTensionByJobId({});
    setCreateInnovationPlatformError(null);
    setCreateInnovationPlatformJobId(null);
    setExpandedTensionDeepDiveId(null);
    setOpenTensionMenuId(null);
    setOpenJobMenuId(null);
    setJobMenuAnchor(null);
    setTensionMenuAnchor(null);
    setPendingPlatformScrollId(null);
    setAddTensionForJob(null);
    setNewTensionDescription('');
    setNewTensionEvidenced(false);
    setAddTensionError(null);
    setShowAddJobModal(false);
    setShowJobsIdentifierModal(false);
    setShowJobsPrioritiserModal(false);
    setShowPlatformPrioritiserModal(false);
    setPrioritiserSelectMode(false);
    setPrioritiserSelections({});
    setShowPlatformPrioritiserConfirm(false);
    setPrioritiserSaving(false);
    setPrioritiserError(null);
    setPrioritiserSuccessNotice(null);
    setShowTensionIdentifierModal(false);
    setTensionIdentifierJob(null);
    setTensionIdentifierPendingService(null);
    setTensionIdentifierBusy(false);
    setTensionIdentifierError(null);
    setJobsIdentifierMarket('');
    setJobsIdentifierPendingService(null);
    setJobsIdentifierBusy(false);
    setJobsIdentifierError(null);
    setJobWhenPart('');
    setJobWantPart('');
    setJobSoCanPart('');
    setJobMarket('');
    setJobHasBeenEvidenced(false);
    setAddJobError(null);
    setEditingJobId(null);
    setEditingTensionId(null);
    setDeleteTarget(null);
    setDeleting(false);
    setDeleteError(null);
    setName('');
    setSelectedMarkets([]);
    setMarketQuery('');
    setMarketResults([]);
    setShowMarketDropdown(false);
    setOpportunitySpace('');
    setGatherProjectInfo(false);
    setShowOpportunityHelpModal(false);
    setJobToBeDone('');
    setTension('');
    setPlatformMarket('');
    setHasBeenEvidenced(false);
    setError(null);
    setFieldErrors({});
    setDrawerWidthPx(null);
    innovationTabWidthRef.current = null;
    prevWorkspaceTabRef.current = 'innovation-platform';
  };

  const resetPlatformForm = () => {
    setJobToBeDone('');
    setTension('');
    setPlatformMarket(createdProject?.markets?.[0] || '');
    setHasBeenEvidenced(false);
    setFieldErrors({});
  };

  const addMarket = (country: Country) => {
    setSelectedMarkets((prev) => (prev.includes(country.name) ? prev : [...prev, country.name]));
    setMarketQuery('');
    setShowMarketDropdown(false);
    if (fieldErrors.markets) setFieldErrors((prev) => ({ ...prev, markets: '' }));
    fetchCountries('');
  };

  const removeMarket = (marketName: string) => {
    setSelectedMarkets((prev) => prev.filter((m) => m !== marketName));
  };

  const validateProject = () => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Project name is required.';
    if (selectedMarkets.length === 0) errors.markets = 'At least one market is required.';
    if (!opportunitySpace.trim()) errors.opportunitySpace = 'Opportunity space is required.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePlatform = () => {
    const errors: Record<string, string> = {};
    if (!jobToBeDone.trim()) errors.jobToBeDone = 'Job to be done is required.';
    if (!tension.trim()) errors.tension = 'Tension is required.';
    if (!platformMarket.trim()) errors.platformMarket = 'Market is required.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isWorkspaceMode && !canCreateProject) {
      setError('You do not have permission to create projects.');
      return;
    }
    if (!validateProject()) return;

    setError(null);
    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    try {
      const response = await fetch('/api/projects/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken || '',
        },
        body: JSON.stringify({
          name: name.trim(),
          markets: selectedMarkets,
          opportunity_space: opportunitySpace.trim(),
          gather_project_info: gatherProjectInfo,
          lifecycle: 'active',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = typeof errorData === 'object'
          ? (errorData.detail || errorData.error || Object.values(errorData).flat().join(', ') || 'Failed to create project')
          : 'Failed to create project';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      const project = await response.json();
      const researchStarted =
        (Array.isArray(project.research_jobs) && project.research_jobs.length > 0) ||
        Boolean(project.research_job);

      if (gatherProjectInfo && project.research_start_warning && !researchStarted) {
        setError(`Project created, but research could not be started: ${project.research_start_warning}`);
      }

      setCreatedProject(project);
      setPlatformMarket(project.markets?.[0] || selectedMarkets[0] || '');
      setDrawerStep('platform-form');
      onProjectContinue?.(project);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while creating the project';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPlatform = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createdProject?.id || !validatePlatform()) return;

    setError(null);
    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    try {
      const existingPlatforms = await fetchPlatforms(createdProject.id);
      const emptyDraft = existingPlatforms.find(
        (platform) => !platform.job_to_be_done?.trim() && !platform.tension?.trim()
      );

      const payload = {
        job_to_be_done: jobToBeDone.trim(),
        tension: tension.trim(),
        market: platformMarket.trim(),
        has_been_evidenced: hasBeenEvidenced,
      };

      const response = emptyDraft
        ? await fetch(`/api/blueprints/${emptyDraft.id}/`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrftoken || '',
            },
            body: JSON.stringify({
              job_to_be_done: payload.job_to_be_done,
              tension: payload.tension,
              has_been_evidenced: payload.has_been_evidenced,
            }),
          })
        : await fetch('/api/blueprints/', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrftoken || '',
            },
            body: JSON.stringify({
              project: createdProject.id,
              ...payload,
              uses_new_workflow: true,
            }),
          });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = typeof errorData === 'object'
          ? (errorData.detail || errorData.error || Object.values(errorData).flat().join(', ') || 'Failed to save innovation platform')
          : 'Failed to save innovation platform';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      const list = await fetchPlatforms(createdProject.id);
      await buildJobItems(createdProject.id, list);
      resetPlatformForm();
      setDrawerStep('platform-list');
      notifyWorkspaceDataChange();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while saving the innovation platform';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const openAddTensionModal = (job: JobItem) => {
    setAddTensionForJob(job);
    setNewTensionDescription('');
    setNewTensionEvidenced(false);
    setAddTensionError(null);
    setEditingTensionId(null);
  };

  const closeAddTensionModal = () => {
    setAddTensionForJob(null);
    setNewTensionDescription('');
    setNewTensionEvidenced(false);
    setAddTensionError(null);
    setEditingTensionId(null);
  };

  const openAddJobModal = () => {
    const markets = createdProject?.markets?.length
      ? createdProject.markets
      : selectedMarkets;
    setShowAddJobModal(true);
    setJobWhenPart('');
    setJobWantPart('');
    setJobSoCanPart('');
    setJobMarket(markets[0] || '');
    setJobHasBeenEvidenced(false);
    setAddJobError(null);
    setEditingJobId(null);
  };

  const closeAddJobModal = () => {
    setShowAddJobModal(false);
    setJobWhenPart('');
    setJobWantPart('');
    setJobSoCanPart('');
    setJobMarket('');
    setJobHasBeenEvidenced(false);
    setAddJobError(null);
    setEditingJobId(null);
  };

  const openEditJobModal = (job: JobItem) => {
    const markets = createdProject?.markets?.length
      ? createdProject.markets
      : selectedMarkets;
    const { when, want, soThat } = splitJobToBeDone(job.text);
    setShowAddJobModal(true);
    setJobWhenPart(when);
    setJobWantPart(want);
    setJobSoCanPart(soThat);
    setJobMarket(job.market || markets[0] || '');
    setJobHasBeenEvidenced(false);
    setAddJobError(null);
    setEditingJobId(job.id);
  };

  const openEditTensionModal = (job: JobItem, tension: JobTensionItem) => {
    setAddTensionForJob(job);
    setNewTensionDescription(tension.text);
    setNewTensionEvidenced(false);
    setAddTensionError(null);
    setEditingTensionId(tension.id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !createdProject?.id) return;
    setDeleting(true);
    setDeleteError(null);
    const csrftoken = getCookie('csrftoken');
    const { kind, job, tension } = deleteTarget;
    try {
      const response = await fetch(
        `/api/projects/${createdProject.id}/hidden-workspace-items/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken || '',
          },
          body: JSON.stringify({
            kind,
            job_to_be_done: job.text,
            market: job.market || '',
            tension_text: kind === 'tension' && tension ? tension.text : '',
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          typeof errorData === 'object'
            ? errorData.error || errorData.detail || 'Failed to delete'
            : 'Failed to delete';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
      setDeleteTarget(null);
      await loadJobsTab();
      notifyWorkspaceDataChange();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const openJobsIdentifierModal = () => {
    const markets = createdProject?.markets?.length ? createdProject.markets : selectedMarkets;
    setJobsIdentifierMarket(markets[0] || '');
    setJobsIdentifierPendingService(null);
    setJobsIdentifierError(null);
    setShowJobsIdentifierModal(true);
  };

  const closeJobsIdentifierModal = () => {
    if (jobsIdentifierBusy) return;
    setShowJobsIdentifierModal(false);
    setJobsIdentifierMarket('');
    setJobsIdentifierPendingService(null);
    setJobsIdentifierError(null);
  };

  const openJobsPrioritiserModal = () => {
    setShowJobsPrioritiserModal(true);
  };

  const closeJobsPrioritiserModal = () => {
    setShowJobsPrioritiserModal(false);
  };

  const openPlatformPrioritiserModal = () => {
    setShowPlatformPrioritiserModal(true);
  };

  const closePlatformPrioritiserModal = () => {
    setShowPlatformPrioritiserModal(false);
  };

  const prioritiserSelectionList = useMemo(
    () => Object.values(prioritiserSelections),
    [prioritiserSelections]
  );
  const prioritiserSelectedCount = prioritiserSelectionList.length;

  const startPlatformPrioritiserSelection = () => {
    setShowPlatformPrioritiserModal(false);
    setShowPlatformPrioritiserConfirm(false);
    setPrioritiserError(null);
    setWorkspaceTab('jobs-to-be-done');
    setPrioritiserSelectMode(true);
  };

  const togglePrioritiserTension = (job: JobItem, tension: JobTensionItem) => {
    const key = `${job.id}::${tension.id}`;
    setPrioritiserSelections((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
        return next;
      }
      if (Object.keys(next).length >= PLATFORM_PRIORITISER_MAX_TENSIONS) {
        return prev;
      }
      next[key] = {
        key,
        jobId: job.id,
        jobText: job.text,
        tensionId: tension.id,
        tensionText: tension.text,
        market: job.market || '',
        blueprintId: job.platformId ?? null,
      };
      return next;
    });
  };

  const cancelPrioritiserSelection = () => {
    setPrioritiserSelectMode(false);
    setPrioritiserSelections({});
    setPrioritiserError(null);
  };

  const confirmPrioritiserSelection = () => {
    if (prioritiserSelectedCount === 0) return;
    setPrioritiserSelectMode(false);
    setShowPlatformPrioritiserConfirm(true);
  };

  const editPrioritiserSelection = () => {
    setShowPlatformPrioritiserConfirm(false);
    setPrioritiserError(null);
    setWorkspaceTab('jobs-to-be-done');
    setPrioritiserSelectMode(true);
  };

  const groupedPrioritiserSelections = useMemo(() => {
    const groups: { jobId: string; jobText: string; tensions: PrioritiserTensionSelection[] }[] = [];
    const indexByJob = new Map<string, number>();
    for (const selection of prioritiserSelectionList) {
      let idx = indexByJob.get(selection.jobId);
      if (idx === undefined) {
        idx = groups.length;
        indexByJob.set(selection.jobId, idx);
        groups.push({ jobId: selection.jobId, jobText: selection.jobText, tensions: [] });
      }
      groups[idx].tensions.push(selection);
    }
    return groups;
  }, [prioritiserSelectionList]);

  const submitPlatformPrioritiserPurchase = async () => {
    if (!createdProject?.id || prioritiserSelectedCount === 0) return;
    const service = PLATFORM_PRIORITISER_SERVICES[0];
    setPrioritiserSaving(true);
    setPrioritiserError(null);
    const csrftoken = getCookie('csrftoken');
    try {
      const response = await fetch(
        `/api/projects/${createdProject.id}/prioritiser-purchases/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken || '',
          },
          body: JSON.stringify({
            kind: 'platform',
            product_name: service.title,
            product_slug: service.id,
            pricing_label: service.pricing,
            selections: prioritiserSelectionList.map((selection) => ({
              job_to_be_done: selection.jobText,
              tension_text: selection.tensionText,
              market: selection.market,
              blueprint_id: selection.blueprintId,
            })),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message =
          typeof errorData === 'object'
            ? (errorData.detail || errorData.error || Object.values(errorData).flat().join(', ') || 'Failed to submit request')
            : 'Failed to submit request';
        throw new Error(typeof message === 'string' ? message : 'Failed to submit request');
      }

      setShowPlatformPrioritiserConfirm(false);
      setPrioritiserSelectMode(false);
      setPrioritiserSelections({});
      setPrioritiserSuccessNotice(
        'Your Platform Prioritiser request has been sent. We will be in touch shortly.'
      );
      notifyWorkspaceDataChange();
    } catch (err: unknown) {
      setPrioritiserError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setPrioritiserSaving(false);
    }
  };

  const openTensionIdentifierModal = (job: JobItem) => {
    setTensionIdentifierJob(job);
    setTensionIdentifierPendingService(null);
    setTensionIdentifierError(null);
    setShowTensionIdentifierModal(true);
  };

  const closeTensionIdentifierModal = () => {
    if (tensionIdentifierBusy) return;
    setShowTensionIdentifierModal(false);
    setTensionIdentifierJob(null);
    setTensionIdentifierPendingService(null);
    setTensionIdentifierError(null);
  };

  const handleTensionIdentifierExplore = (serviceId: TensionIdentifierServiceId) => {
    if (!tensionIdentifierJob?.text.trim()) {
      setTensionIdentifierError('This job is missing a job to be done.');
      return;
    }
    if (!(tensionIdentifierJob.market || '').trim()) {
      setTensionIdentifierError('This job is missing a market.');
      return;
    }
    setTensionIdentifierError(null);
    setTensionIdentifierPendingService(serviceId);
  };

  const cancelTensionIdentifierConfirm = () => {
    if (tensionIdentifierBusy) return;
    setTensionIdentifierPendingService(null);
    setTensionIdentifierError(null);
  };

  const confirmTensionIdentifierGenerate = async () => {
    if (
      !createdProject?.id ||
      !tensionIdentifierJob ||
      !tensionIdentifierPendingService
    ) {
      return;
    }

    const jobText = tensionIdentifierJob.text.trim();
    const market = (tensionIdentifierJob.market || '').trim();
    if (!jobText || !market) return;

    setTensionIdentifierBusy(true);
    setTensionIdentifierError(null);
    const csrftoken = getCookie('csrftoken');

    try {
      const response = await fetch(
        `/api/projects/${createdProject.id}/tension-identifier-generate/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken || '',
          },
          body: JSON.stringify({
            market,
            job_to_be_done: jobText,
            service: tensionIdentifierPendingService,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));
      const alreadyGenerated = response.status === 409;

      if (!response.ok && !alreadyGenerated) {
        const errorMessage = typeof data === 'object'
          ? (data.error || data.detail || 'Failed to generate tensions')
          : 'Failed to generate tensions';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      await refreshJobTensions(tensionIdentifierJob);
      notifyWorkspaceDataChange();

      setTensionIdentifierPendingService(null);
      setShowTensionIdentifierModal(false);
      setTensionIdentifierJob(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while generating tensions';
      setTensionIdentifierError(message);
    } finally {
      setTensionIdentifierBusy(false);
    }
  };

  const pendingTensionIdentifierService = tensionIdentifierPendingService
    ? TENSION_IDENTIFIER_SERVICES.find((item) => item.id === tensionIdentifierPendingService) ?? null
    : null;

  const handleJobsIdentifierExplore = (serviceId: JobsIdentifierServiceId) => {
    if (!jobsIdentifierMarket.trim()) {
      setJobsIdentifierError('Select a market before continuing.');
      return;
    }
    setJobsIdentifierError(null);
    setJobsIdentifierPendingService(serviceId);
  };

  const cancelJobsIdentifierConfirm = () => {
    if (jobsIdentifierBusy) return;
    setJobsIdentifierPendingService(null);
    setJobsIdentifierError(null);
  };

  const confirmJobsIdentifierGenerate = async () => {
    if (!createdProject?.id || !jobsIdentifierPendingService || !jobsIdentifierMarket.trim()) return;

    const service = JOBS_IDENTIFIER_SERVICES.find((item) => item.id === jobsIdentifierPendingService);
    if (!service) return;

    setJobsIdentifierBusy(true);
    setJobsIdentifierError(null);
    const csrftoken = getCookie('csrftoken');

    try {
      const response = await fetch(
        `/api/projects/${createdProject.id}/jobs-identifier-generate/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken || '',
          },
          body: JSON.stringify({
            market: jobsIdentifierMarket.trim(),
            service: jobsIdentifierPendingService,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));
      const alreadyGenerated = response.status === 409;

      if (!response.ok && !alreadyGenerated) {
        const errorMessage = typeof data === 'object'
          ? (data.error || data.detail || 'Failed to generate jobs to be done')
          : 'Failed to generate jobs to be done';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      await fetchPlatforms(createdProject.id);
      await loadJobsTab();
      setWorkspaceTab('jobs-to-be-done');
      notifyWorkspaceDataChange();

      setJobsIdentifierPendingService(null);
      setShowJobsIdentifierModal(false);
      setJobsIdentifierMarket('');

      if (alreadyGenerated) {
        setJobsIdentifierError(null);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while generating jobs';
      setJobsIdentifierError(message);
    } finally {
      setJobsIdentifierBusy(false);
    }
  };

  const pendingJobsIdentifierService = jobsIdentifierPendingService
    ? JOBS_IDENTIFIER_SERVICES.find((item) => item.id === jobsIdentifierPendingService) ?? null
    : null;

  const handleSubmitNewJob = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createdProject?.id) return;

    if (!jobWhenPart.trim() || !jobWantPart.trim() || !jobSoCanPart.trim()) {
      setAddJobError('When I\'m, I want, and So that are all required.');
      return;
    }
    if (!jobMarket.trim()) {
      setAddJobError('Market is required.');
      return;
    }

    const jobToBeDone = buildJobToBeDoneText(jobWhenPart, jobWantPart, jobSoCanPart);

    setAddJobError(null);
    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    try {
      const response = await fetch('/api/blueprints/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken || '',
        },
        body: JSON.stringify({
          project: createdProject.id,
          job_to_be_done: jobToBeDone,
          tension: '',
          market: jobMarket.trim(),
          has_been_evidenced: jobHasBeenEvidenced,
          uses_new_workflow: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = typeof errorData === 'object'
          ? (errorData.detail || errorData.error || Object.values(errorData).flat().join(', ') || 'Failed to add job')
          : 'Failed to add job';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      await fetchPlatforms(createdProject.id);
      await loadJobsTab();
      closeAddJobModal();
      notifyWorkspaceDataChange();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while adding the job';
      setAddJobError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshJobTensions = async (job: JobItem) => {
    if (!createdProject?.id) return;
    const platform = job.platformId ? platforms.find((p) => p.id === job.platformId) : undefined;
    const tensions = await fetchTensionsForJob(
      createdProject.id,
      job.text,
      job.market || '',
      platforms,
      platform
    );
    markTensionsLinkedToPlatforms([{ ...job, tensions }], platforms);
    setJobItems((prev) =>
      prev.map((item) =>
        item.id === job.id ? { ...item, tensions, tensionCount: tensions.length } : item
      )
    );
  };

  const openInnovationPlatformForMatch = (jobText: string, tensionText: string, market?: string) => {
    const existing = platforms.find((platform) =>
      platformMatchesJobTension(platform, jobText, tensionText, market)
    );
    if (!existing) return false;
    setWorkspaceTab('innovation-platform');
    setDrawerStep('platform-list');
    setExpandedPlatformId(existing.id);
    setPendingPlatformScrollId(existing.id);
    return true;
  };

  const handleCreateInnovationPlatform = async (job: JobItem) => {
    if (!createdProject?.id) return;

    const selectedTensionId = selectedTensionByJobId[job.id];
    const selectedTension = job.tensions.find((tension) => tension.id === selectedTensionId);
    if (!selectedTension) {
      setCreateInnovationPlatformJobId(job.id);
      setCreateInnovationPlatformError('Select a tension before creating an innovation platform.');
      return;
    }

    const jobText = job.text.trim();
    const tensionText = selectedTension.text.trim();
    const market = (job.market || '').trim();

    if (!jobText || !tensionText) {
      setCreateInnovationPlatformJobId(job.id);
      setCreateInnovationPlatformError('Job and tension are required.');
      return;
    }

    if (!market) {
      setCreateInnovationPlatformJobId(job.id);
      setCreateInnovationPlatformError('This job is missing a market.');
      return;
    }

    if (openInnovationPlatformForMatch(jobText, tensionText, market)) {
      setCreateInnovationPlatformError(null);
      setCreateInnovationPlatformJobId(null);
      notifyWorkspaceDataChange();
      return;
    }

    setCreateInnovationPlatformError(null);
    setCreateInnovationPlatformJobId(null);
    setCreatingPlatformJobId(job.id);
    const csrftoken = getCookie('csrftoken');

    try {
      const payload = {
        job_to_be_done: jobText,
        tension: tensionText,
        market,
        has_been_evidenced: Boolean(selectedTension.evidenced),
      };

      const response = await fetch('/api/blueprints/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken || '',
        },
        body: JSON.stringify({
          project: createdProject.id,
          ...payload,
          uses_new_workflow: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = typeof errorData === 'object'
          ? (errorData.detail || errorData.error || Object.values(errorData).flat().join(', ') || 'Failed to create innovation platform')
          : 'Failed to create innovation platform';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      const saved = await response.json();
      const list = await fetchPlatforms(createdProject.id);
      const nextItems = await buildJobItems(createdProject.id, list);
      const rebuiltJob =
        nextItems.find((item) => item.id === job.id) ||
        nextItems.find(
          (item) =>
            jobTextsMatch(item.text, jobText) &&
            (item.market || '').trim() === market
        );
      const nextUnlinkedTension = rebuiltJob?.tensions.find((tension) => !tension.platformLinked);

      setSelectedTensionByJobId((prev) => {
        const next = { ...prev };
        delete next[job.id];
        if (rebuiltJob && nextUnlinkedTension) {
          next[rebuiltJob.id] = nextUnlinkedTension.id;
        }
        return next;
      });
      setDrawerStep('platform-list');
      if (rebuiltJob && nextUnlinkedTension) {
        setExpandedJobId(rebuiltJob.id);
      } else {
        setWorkspaceTab('innovation-platform');
        setExpandedPlatformId(saved.id);
        setPendingPlatformScrollId(saved.id);
      }
      notifyWorkspaceDataChange();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while creating the innovation platform';
      setCreateInnovationPlatformJobId(job.id);
      setCreateInnovationPlatformError(message);
    } finally {
      setCreatingPlatformJobId(null);
    }
  };

  const handleSubmitNewTension = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createdProject?.id || !addTensionForJob) return;

    const description = newTensionDescription.trim();
    if (!description) {
      setAddTensionError('Description is required.');
      return;
    }

    setAddTensionError(null);
    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    try {
      const response = await fetch(
        `/api/projects/${createdProject.id}/job-tensions/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken || '',
          },
          body: JSON.stringify({
            job_to_be_done: addTensionForJob.text.trim(),
            market: (addTensionForJob.market || '').trim(),
            description,
            has_been_evidenced: newTensionEvidenced,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = typeof errorData === 'object'
          ? (errorData.detail || errorData.error || Object.values(errorData).flat().join(', ') || 'Failed to add tension')
          : 'Failed to add tension';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      await refreshJobTensions(addTensionForJob);
      closeAddTensionModal();
      notifyWorkspaceDataChange();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred while adding the tension';
      setAddTensionError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isWorkspaceMode && createdProject && drawerStep !== 'project') {
      onProjectCreated?.(createdProject);
    }
    resetAll();
    onClose();
  };

  const availableResults = marketResults.filter((c) => !selectedMarkets.includes(c.name));
  const projectMarkets = createdProject?.markets?.length
    ? createdProject.markets
    : selectedMarkets;

  const sortedJobItems = useMemo(() => {
    const rank = (s: number) => (s > 0 ? s : Number.POSITIVE_INFINITY);
    return [...jobItems].sort((a, b) => {
      if (rank(a.score) !== rank(b.score)) return rank(a.score) - rank(b.score);
      if (a.score === 0 && b.score === 0) {
        const src = (j: JobItem) => (j.source === 'catalyx-ai' ? 0 : 1);
        if (src(a) !== src(b)) return src(a) - src(b);
      }
      return 0;
    });
  }, [jobItems]);

  const handleJobMenuAction = (label: string) => {
    const job = jobItems.find((item) => item.id === openJobMenuId);
    closeActionMenus();
    if (!job) return;
    if (label === 'Edit') {
      openEditJobModal(job);
    } else if (label === 'Delete') {
      setDeleteError(null);
      setDeleteTarget({ kind: 'job', job });
    }
  };

  const handleTensionMenuAction = (label: string) => {
    let foundJob: JobItem | undefined;
    let foundTension: JobTensionItem | undefined;
    for (const job of jobItems) {
      const tension = job.tensions.find((item) => item.id === openTensionMenuId);
      if (tension) {
        foundJob = job;
        foundTension = tension;
        break;
      }
    }
    closeActionMenus();
    if (!foundJob || !foundTension) return;
    if (label === 'Edit') {
      openEditTensionModal(foundJob, foundTension);
    } else if (label === 'Delete') {
      setDeleteError(null);
      setDeleteTarget({ kind: 'tension', job: foundJob, tension: foundTension });
    }
  };

  const jobSourceBadge = (job: JobItem) => (
    <span
      className={`shrink-0 px-2.5 py-1 rounded text-[11px] font-medium whitespace-nowrap ${
        job.source === 'catalyx-ai'
          ? 'bg-lime-500 text-white'
          : 'bg-gray-200 text-gray-600'
      }`}
    >
      {job.source === 'catalyx-ai'
        ? 'Catalyx AI'
        : job.evidenced
          ? 'User Added • Evidenced'
          : 'User Added'}
    </span>
  );

  const tensionSourceBadge = (tension: JobTensionItem) => {
    if (tension.source === 'catalyx-ai') {
      return (
        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-lime-500 text-white">
          Catalyx AI
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-600">
        {tension.evidenced ? 'User Added • Evidenced' : 'User Added'}
      </span>
    );
  };

  const jobActionMenuPortal =
    openJobMenuId && jobMenuAnchor
      ? createPortal(
          <div
            data-job-menu
            className="fixed z-[120] w-40 rounded-lg border border-[#E0DEE8] bg-white shadow-lg py-1 text-sm text-gray-700"
            style={{ top: jobMenuAnchor.top, right: jobMenuAnchor.right }}
          >
            {[
              { label: 'Edit', icon: Pencil },
              { label: 'Move', icon: ArrowRightLeft },
              { label: 'Delete', icon: Trash2 },
            ].map(({ label, icon: Icon }) => (
              <button
                key={label}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2.5"
                onClick={() => handleJobMenuAction(label)}
              >
                <Icon className="w-4 h-4 text-gray-500" />
                {label}
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  const tensionActionMenuPortal =
    openTensionMenuId && tensionMenuAnchor
      ? createPortal(
          <div
            data-job-menu
            className="fixed z-[120] w-36 rounded-lg border border-[#E0DEE8] bg-white shadow-lg py-1 text-sm text-gray-700"
            style={{ top: tensionMenuAnchor.top, right: tensionMenuAnchor.right }}
          >
            {['Edit', 'Move', 'Delete'].map((label) => (
              <button
                key={label}
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => handleTensionMenuAction(label)}
              >
                {label}
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  const showPlatformForm =
    drawerStep === 'platform-form' || !hasInnovationPlatformEntries(platforms);

  if (!isOpen) return null;

  if (isWorkspaceMode && isLoading && !createdProject) {
    return (
      <div className="fixed inset-0 z-[100] flex justify-start pointer-events-none">
        {renderDrawerShell(
          <p className="text-sm text-gray-500 px-8">Loading Innovation Workspace…</p>,
          { center: true }
        )}
      </div>
    );
  }

  if (isWorkspaceMode && !createdProject) {
    return (
      <div className="fixed inset-0 z-[100] flex justify-start pointer-events-none">
        {renderDrawerShell(
          <div className="flex flex-col items-center justify-center px-8 gap-4 flex-1">
            <p className="text-sm text-red-600 text-center">{error || 'Could not load this project.'}</p>
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 rounded-full border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>,
          { center: true }
        )}
      </div>
    );
  }

  const tabClass = (tab: WorkspaceTab) =>
    `flex-1 py-2.5 text-sm text-center border border-[#E0DEE8] transition-colors ${
      workspaceTab === tab
        ? 'bg-white text-[#43C8F5] font-medium border-t-2 border-t-[#43C8F5] border-b-white -mb-px z-10 relative'
        : 'bg-[#F3F2F8] text-gray-900 font-bold hover:bg-gray-100'
    }`;

  const nestedModalShellClass = isWorkspaceMode
    ? 'fixed inset-0 z-[110] flex items-center justify-center p-6 pointer-events-none'
    : 'fixed inset-0 z-[110] flex items-center justify-center p-6 pointer-events-auto';

  const nestedModalBackdropClass = isWorkspaceMode
    ? 'absolute inset-0 bg-black/40 pointer-events-none'
    : 'absolute inset-0 bg-black/40';

  const nestedModalPanelClass = `relative w-full max-w-lg rounded-2xl bg-white shadow-2xl ${
    isWorkspaceMode ? 'pointer-events-auto' : ''
  }`;

  return (
    <>
    <div className={`fixed inset-0 z-[100] flex justify-start ${isWorkspaceMode ? 'pointer-events-none' : ''}`}>
      {!isWorkspaceMode && (
        <div
          className="absolute inset-0 bg-black/30 animate-in fade-in duration-200 pointer-events-auto"
          onClick={handleClose}
          aria-hidden
        />
      )}

      {renderDrawerShell(
        drawerStep === 'project' && !isWorkspaceMode ? (
          <>
            <div className="flex items-start justify-between px-8 pt-8 pb-4">
              <div>
                <h2 id="create-project-title" className="text-xl font-bold text-gray-900">
                  Create a project
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Define the market context for this project.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateProject} className="flex flex-col px-8 overflow-y-auto pb-6">
              <div className="space-y-6">
                <div>
                  <label htmlFor="project-name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Project name<span className="text-gray-900">*</span>
                  </label>
                  <input
                    id="project-name"
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: '' }));
                    }}
                    className={inputClass}
                    placeholder="Enter your project name"
                    disabled={isLoading}
                  />
                  {fieldErrors.name && <p className="mt-1.5 text-xs text-red-600">{fieldErrors.name}</p>}
                </div>

                <div ref={marketContainerRef}>
                  <label htmlFor="project-markets" className="block text-sm font-semibold text-gray-900 mb-2">
                    Market(s)<span className="text-gray-900">*</span>
                  </label>
                  {selectedMarkets.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedMarkets.map((market) => (
                        <span
                          key={market}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#655FA8] text-[#655FA8] text-xs font-medium bg-white"
                        >
                          {market}
                          <button type="button" onClick={() => removeMarket(market)} className="text-[#655FA8]/70 hover:text-[#655FA8]">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      id="project-markets"
                      type="text"
                      value={marketQuery}
                      onChange={(e) => {
                        setMarketQuery(e.target.value);
                        setShowMarketDropdown(true);
                        if (fieldErrors.markets) setFieldErrors((prev) => ({ ...prev, markets: '' }));
                      }}
                      onFocus={() => {
                        setShowMarketDropdown(true);
                        fetchCountries(marketQuery);
                      }}
                      className={`${inputClass} pr-10`}
                      placeholder="Search by country name or code"
                      disabled={isLoading}
                      autoComplete="off"
                    />
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    {showMarketDropdown && (
                      <div className="absolute z-20 w-full mt-1.5 bg-white border border-[#E0DEE8] rounded-xl shadow-xl max-h-48 overflow-y-auto">
                        {isSearchingMarkets ? (
                          <p className="px-4 py-3 text-sm text-gray-400">Searching...</p>
                        ) : availableResults.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-gray-400">No countries found</p>
                        ) : (
                          availableResults.map((country) => (
                            <button
                              key={country.id}
                              type="button"
                              onClick={() => addMarket(country)}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-[#43C8F5]/10"
                            >
                              <span className="font-medium">{country.name}</span>
                              <span className="ml-2 text-xs text-gray-400">{country.alpha_2} · {country.alpha_3}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {fieldErrors.markets && <p className="mt-1.5 text-xs text-red-600">{fieldErrors.markets}</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label htmlFor="opportunity-space" className="text-sm font-semibold text-gray-900">
                      Opportunity space<span className="text-gray-900">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowOpportunityHelpModal(true)}
                      className="text-sm font-medium text-[#43C8F5] underline hover:text-[#2A9CC4] transition-colors shrink-0"
                    >
                      Need help?
                    </button>
                  </div>
                  <textarea
                    id="opportunity-space"
                    value={opportunitySpace}
                    onChange={(e) => {
                      setOpportunitySpace(e.target.value);
                      if (fieldErrors.opportunitySpace) setFieldErrors((prev) => ({ ...prev, opportunitySpace: '' }));
                    }}
                    rows={5}
                    className={`${inputClass} resize-y min-h-[120px]`}
                    placeholder="e.g. Adults looking for a non-alcoholic drink to help them unwind in the evening without feeling excluded in social situations."
                    disabled={isLoading}
                  />
                  {fieldErrors.opportunitySpace && (
                    <p className="mt-1.5 text-xs text-red-600">{fieldErrors.opportunitySpace}</p>
                  )}
                </div>

                <div className="flex items-center justify-between gap-4">
                  {can('ai_source_of_truth_tick') ? (
                  <label className="flex items-center gap-3 cursor-pointer group min-w-0">
                    <input
                      type="checkbox"
                      checked={gatherProjectInfo}
                      onChange={(e) => setGatherProjectInfo(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#43C8F5] focus:ring-[#43C8F5]/40 shrink-0"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-gray-500 group-hover:text-gray-700">Start Deep Research</span>
                  </label>
                  ) : (
                    <span />
                  )}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex items-center justify-center rounded-full border border-[#43C8F5] bg-white px-10 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  >
                    {isLoading ? 'Creating...' : 'Continue'}
                  </button>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
                )}
              </div>
            </form>
          </>
        ) : (
          <>
            {/* Innovation Workspace header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-2">
              <div className="min-w-0 pr-4">
                <h2 className="text-xl font-bold text-gray-900">Innovation Workspace</h2>
                <p className="text-sm text-gray-400 mt-0.5 truncate">
                  {createdProject?.name || 'Project Name'}
                </p>
              </div>
              {isWorkspaceMode ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 transition-colors"
                  aria-label="Close workspace"
                  title="Close workspace"
                >
                  <X className="w-5 h-5" />
                </button>
              ) : null}
            </div>

            {/* Tabs */}
            <div className="flex px-6 pt-3 gap-0 border-b border-[#E0DEE8]">
              <button type="button" className={`${tabClass('innovation-platform')} rounded-tl-lg`} onClick={() => setWorkspaceTab('innovation-platform')}>
                Innovation Platform
              </button>
              <button type="button" className={`${tabClass('jobs-to-be-done')} rounded-tr-lg border-l-0`} onClick={() => setWorkspaceTab('jobs-to-be-done')}>
                Jobs To Be Done
              </button>
            </div>

            {workspaceTab === 'jobs-to-be-done' ? (
              <div className="flex-1 flex flex-col px-6 overflow-hidden min-h-0">
                <div className="flex flex-wrap items-center justify-between gap-3 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className={`${workspacePurplePillClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                      onClick={openJobsIdentifierModal}
                      disabled={prioritiserSelectMode}
                    >
                      Jobs Identifier
                    </button>
                    <button
                      type="button"
                      className={`${workspacePurplePillClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                      onClick={openJobsPrioritiserModal}
                      disabled={prioritiserSelectMode}
                    >
                      Jobs Prioritiser
                    </button>
                    <button
                      type="button"
                      className={`${workspacePurplePillClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                      onClick={openPlatformPrioritiserModal}
                      disabled={prioritiserSelectMode}
                    >
                      Platform Prioritiser
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={openAddJobModal}
                    className={`${workspaceOutlinedButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                    disabled={prioritiserSelectMode}
                  >
                    <Plus className="w-4 h-4 shrink-0" />
                    Add a Job To Be Done
                  </button>
                </div>

                {prioritiserSelectMode ? (
                  <div className="flex items-center justify-between gap-3 mb-4 rounded-lg border border-[#43C8F5]/40 bg-[#43C8F5]/10 px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Info className="w-4 h-4 text-[#2A9CC4] shrink-0" aria-hidden />
                      <span className="text-sm text-gray-700">
                        Select up to {PLATFORM_PRIORITISER_MAX_TENSIONS} tensions to verify with Catalyx Crowd
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold text-gray-700">
                        {prioritiserSelectedCount}/{PLATFORM_PRIORITISER_MAX_TENSIONS} Selected
                      </span>
                      <button
                        type="button"
                        onClick={cancelPrioritiserSelection}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded"
                        aria-label="Cancel selection"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="flex items-center gap-3 pb-4">
                  <div className="relative flex-1 min-w-0">
                    <select className="w-full appearance-none rounded-lg border border-[#E0DEE8] bg-white pl-4 pr-9 py-2 text-sm text-gray-700">
                      <option>Filter</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="relative flex-1 min-w-0">
                    <select className="w-full appearance-none rounded-lg border border-[#E0DEE8] bg-white pl-4 pr-9 py-2 text-sm text-gray-700">
                      <option>Sort</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div
                  ref={jobsListScrollRef}
                  onScroll={handleJobsListScroll}
                  className="flex-1 overflow-y-auto space-y-2 pb-6 min-h-0"
                >
                  {jobsLoading ? (
                    <p className="text-sm text-gray-500 py-8 text-center">Loading jobs…</p>
                  ) : jobItems.length === 0 ? (
                    <p className="text-sm text-gray-500 py-8 text-center">
                      No jobs to be done yet. Add one using the button above.
                    </p>
                  ) : (
                    sortedJobItems.map((job) => {
                      const isExpanded = expandedJobId === job.id;
                      const isHighlighted = highlightedJobId === job.id;
                      return (
                        <div
                          key={job.id}
                          ref={isHighlighted ? highlightedJobRef : undefined}
                          className={`rounded-xl border bg-white shadow-sm transition-colors ${
                            isHighlighted
                              ? 'border-[#43C8F5] ring-2 ring-[#43C8F5]/30'
                              : 'border-[#E0DEE8]'
                          }`}
                        >
                          {isExpanded ? (
                            <>
                              <div className="px-4 pt-4 pb-3">
                                <div className="flex items-start gap-3">
                                  <button
                                    type="button"
                                    className="mt-1 p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing"
                                    aria-label="Reorder job"
                                  >
                                    <GripVertical className="w-4 h-4" />
                                  </button>

                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-gray-900 leading-relaxed break-words pr-2">
                                      {job.text}
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                                      <span>Added by: {job.addedBy || '—'}</span>
                                      <span>Created on: {formatJobDate(job.createdOn)}</span>
                                      <span>Market: {job.market || '—'}</span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    {jobSourceBadge(job)}
                                    <ScoreDot score={job.score} />
                                    <div className="relative" data-job-menu>
                                      <button
                                        type="button"
                                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                        aria-label="Job actions"
                                        onClick={(e) => openJobActionMenu(job.id, e)}
                                      >
                                        <MoreHorizontal className="w-4 h-4" />
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setExpandedJobId(null);
                                        setExpandedTensionDeepDiveId(null);
                                      }}
                                      className="p-1 text-gray-400 hover:text-gray-600"
                                      aria-label="Collapse job"
                                    >
                                      <ChevronDown className="w-4 h-4 rotate-180 transition-transform" />
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div className="px-4 pb-4 ml-7">
                                <h3 className="text-sm font-bold text-gray-900 mb-3">Generated Tensions</h3>
                                {job.tensions.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic">No tensions yet. Add one using the button below.</p>
                                ) : (
                                  <ul className="space-y-3">
                                    {job.tensions.map((tension) => {
                                      const showDeepDive = expandedTensionDeepDiveId === tension.id;
                                      const isPlatformCreated = Boolean(tension.platformLinked);
                                      const isSelected =
                                        !isPlatformCreated && selectedTensionByJobId[job.id] === tension.id;
                                      return (
                                        <li
                                          key={tension.id}
                                          className={`rounded-lg border p-3 transition-colors ${
                                            isPlatformCreated
                                              ? 'border-[#EEEEEE] bg-gray-50'
                                              : isSelected
                                                ? 'border-[#655FA8] ring-1 ring-[#655FA8]/20 bg-white'
                                                : 'border-[#EEEEEE] bg-white'
                                          }`}
                                        >
                                          <div className="flex items-start gap-3">
                                            {isPlatformCreated ? (
                                              <span
                                                className="mt-0.5 w-5 h-5 rounded-full bg-gray-300 border-2 border-gray-300 shrink-0"
                                                title="Already created as innovation platform"
                                                aria-hidden
                                              />
                                            ) : (
                                              <label className="mt-0.5 shrink-0 cursor-pointer">
                                                <input
                                                  type="radio"
                                                  name={`job-tension-${job.id}`}
                                                  checked={isSelected}
                                                  onChange={() => {
                                                    setSelectedTensionByJobId((prev) => ({
                                                      ...prev,
                                                      [job.id]: tension.id,
                                                    }));
                                                    if (createInnovationPlatformJobId === job.id) {
                                                      setCreateInnovationPlatformError(null);
                                                      setCreateInnovationPlatformJobId(null);
                                                    }
                                                  }}
                                                  className="w-5 h-5 text-[#655FA8] border-gray-300 focus:ring-[#655FA8]/40 cursor-pointer"
                                                  aria-label={`Select tension: ${tension.text}`}
                                                />
                                              </label>
                                            )}

                                            <div className="flex-1 min-w-0">
                                              <p
                                                className={`text-sm leading-snug ${
                                                  isPlatformCreated ? 'text-gray-400' : 'text-gray-800'
                                                }`}
                                              >
                                                {tension.text}
                                              </p>

                                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                                {tensionSourceBadge(tension)}
                                                <ScoreDot score={tension.score} />
                                                {isPlatformCreated ? (
                                                  <span className="text-[11px] font-medium text-gray-400">
                                                    Innovation platform created
                                                  </span>
                                                ) : null}
                                              </div>

                                              {isPlatformCreated ? (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    openInnovationPlatformForMatch(job.text, tension.text, job.market);
                                                  }}
                                                  className={`${workspaceLinkClass} mt-2 text-xs`}
                                                >
                                                  View Innovation Platform
                                                </button>
                                              ) : null}

                                              {tension.deepDive && showDeepDive && (
                                                <div className="mt-3 rounded-lg border border-[#E0DEE8] bg-[#FAFAFA] p-3">
                                                  <p className="text-xs font-semibold text-gray-900 mb-1">
                                                    Tension deep dive
                                                  </p>
                                                  <p className="text-xs text-gray-600 leading-relaxed">
                                                    {tension.deepDive}
                                                  </p>
                                                </div>
                                              )}
                                            </div>

                                            <div className="flex items-center gap-1 shrink-0">
                                              {prioritiserSelectMode ? (
                                                (() => {
                                                  const prioritiserKey = `${job.id}::${tension.id}`;
                                                  const prioritiserChecked = Boolean(prioritiserSelections[prioritiserKey]);
                                                  const prioritiserAtLimit =
                                                    !prioritiserChecked &&
                                                    prioritiserSelectedCount >= PLATFORM_PRIORITISER_MAX_TENSIONS;
                                                  return (
                                                    <button
                                                      type="button"
                                                      onClick={() => togglePrioritiserTension(job, tension)}
                                                      disabled={prioritiserAtLimit}
                                                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0 ${
                                                        prioritiserChecked
                                                          ? 'bg-[#655FA8] border-[#655FA8] text-white'
                                                          : 'bg-white border-[#D1D1D1] text-transparent hover:border-[#655FA8]'
                                                      } ${prioritiserAtLimit ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                      aria-label={
                                                        prioritiserChecked
                                                          ? `Deselect tension: ${tension.text}`
                                                          : `Select tension: ${tension.text}`
                                                      }
                                                      aria-pressed={prioritiserChecked}
                                                    >
                                                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                                                    </button>
                                                  );
                                                })()
                                              ) : null}
                                              <div className="relative" data-job-menu>
                                                <button
                                                  type="button"
                                                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                                  aria-label="Tension actions"
                                                  onClick={(e) => openTensionActionMenu(tension.id, e)}
                                                >
                                                  <MoreHorizontal className="w-4 h-4" />
                                                </button>
                                              </div>
                                              {tension.deepDive ? (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setExpandedTensionDeepDiveId(
                                                      showDeepDive ? null : tension.id
                                                    )
                                                  }
                                                  className="p-1 text-gray-400 hover:text-gray-600"
                                                  aria-label={showDeepDive ? 'Collapse tension' : 'Expand tension'}
                                                >
                                                  <ChevronDown
                                                    className={`w-4 h-4 transition-transform ${showDeepDive ? 'rotate-180' : ''}`}
                                                  />
                                                </button>
                                              ) : null}
                                            </div>
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}

                                <div className="mt-4 pt-4 border-t border-[#EEEEEE] flex flex-col gap-3">
                                  {createInnovationPlatformJobId === job.id && createInnovationPlatformError ? (
                                    <p className="text-sm text-red-600">{createInnovationPlatformError}</p>
                                  ) : null}
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <button
                                        type="button"
                                        className={workspacePurplePillClass}
                                        onClick={() => openTensionIdentifierModal(job)}
                                      >
                                        Tension Identifier
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openAddTensionModal(job)}
                                        className={workspaceOutlinedButtonClass}
                                      >
                                        <Plus className="w-4 h-4 shrink-0" />
                                        Add a Tension
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => void handleCreateInnovationPlatform(job)}
                                      disabled={
                                        creatingPlatformJobId != null ||
                                        job.tensions.every((tension) => Boolean(tension.platformLinked))
                                      }
                                      className={`${workspaceOutlinedButtonClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                      <Plus className="w-4 h-4 shrink-0" />
                                      Create Innovation Platform
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-3 px-3 py-3">
                              <button
                                type="button"
                                className="shrink-0 p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing"
                                aria-label="Reorder job"
                              >
                                <GripVertical className="w-4 h-4" />
                              </button>

                              <p className="flex-1 min-w-0 text-sm font-bold text-gray-900 leading-relaxed break-words whitespace-normal">
                                {job.text}
                              </p>

                              <div className="flex shrink-0 items-center gap-2 self-center">
                                {jobSourceBadge(job)}
                                <ScoreDot score={job.score} />
                                <div className="relative flex items-center gap-1" data-job-menu>
                                  <button
                                    type="button"
                                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                    aria-label="Job actions"
                                    onClick={(e) => openJobActionMenu(job.id, e)}
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setExpandedJobId(job.id);
                                      setExpandedTensionDeepDiveId(null);
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    aria-label="Expand job"
                                  >
                                    <ChevronDown className="w-4 h-4 transition-transform" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {prioritiserSelectMode ? (
                  <div className="flex items-center justify-between gap-3 py-4 border-t border-[#EEEEEE]">
                    <button
                      type="button"
                      onClick={cancelPrioritiserSelection}
                      className="inline-flex items-center justify-center rounded-full border border-[#43C8F5] bg-white px-8 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors"
                    >
                      Exit
                    </button>
                    <div className="flex items-center gap-4">
                      {prioritiserSelectedCount === 0 ? (
                        <span className="text-sm text-gray-500">Select at least 1 Tension to continue</span>
                      ) : null}
                      <button
                        type="button"
                        onClick={confirmPrioritiserSelection}
                        disabled={prioritiserSelectedCount === 0}
                        className="inline-flex items-center justify-center rounded-full bg-[#655FA8] px-8 py-2 text-sm font-bold text-white hover:bg-[#544fa0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Confirm Selection
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : showPlatformForm ? (
              <form onSubmit={handleSubmitPlatform} className="flex-1 flex flex-col px-6 overflow-y-auto min-h-0">
                <p className="text-sm text-gray-500 mt-4 mb-5">
                  Get started by adding your JTBD and Tension, or we can help you create them
                </p>

                <div className="space-y-5 pb-6">
                  <div>
                    <label htmlFor="jtbd" className="block text-sm font-semibold text-gray-500 mb-2">
                      Job To Be Done<span className="text-gray-500">*</span>
                    </label>
                    <textarea
                      id="jtbd"
                      value={jobToBeDone}
                      onChange={(e) => {
                        setJobToBeDone(e.target.value);
                        if (fieldErrors.jobToBeDone) setFieldErrors((prev) => ({ ...prev, jobToBeDone: '' }));
                      }}
                      className={textareaClass}
                      placeholder="E.g: When I'm planning a Friday or Saturday night in with my partner or family."
                      disabled={isLoading}
                    />
                    {fieldErrors.jobToBeDone && <p className="mt-1.5 text-xs text-red-600">{fieldErrors.jobToBeDone}</p>}
                  </div>

                  <div>
                    <label htmlFor="tension" className="block text-sm font-semibold text-gray-500 mb-2">
                      Tension<span className="text-gray-500">*</span>
                    </label>
                    <textarea
                      id="tension"
                      value={tension}
                      onChange={(e) => {
                        setTension(e.target.value);
                        if (fieldErrors.tension) setFieldErrors((prev) => ({ ...prev, tension: '' }));
                      }}
                      className={textareaClass}
                      placeholder="E.g: I want a meal that feels more special than a regular meal."
                      disabled={isLoading}
                    />
                    {fieldErrors.tension && <p className="mt-1.5 text-xs text-red-600">{fieldErrors.tension}</p>}
                  </div>

                  <div>
                    <label htmlFor="platform-market" className="block text-sm font-semibold text-gray-500 mb-2">
                      Market<span className="text-gray-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="platform-market"
                        value={platformMarket}
                        onChange={(e) => {
                          setPlatformMarket(e.target.value);
                          if (fieldErrors.platformMarket) setFieldErrors((prev) => ({ ...prev, platformMarket: '' }));
                        }}
                        className={`${inputClass} appearance-none pr-10 cursor-pointer ${platformMarket ? 'text-gray-900' : 'text-gray-400'}`}
                        disabled={isLoading}
                      >
                        <option value="">Select market</option>
                        {projectMarkets.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                    {fieldErrors.platformMarket && (
                      <p className="mt-1.5 text-xs text-red-600">{fieldErrors.platformMarket}</p>
                    )}
                  </div>

                  <div className="pt-1 flex items-center justify-between gap-4">
                    <label className="flex items-center gap-3 cursor-pointer min-w-0">
                      <input
                        type="checkbox"
                        checked={hasBeenEvidenced}
                        onChange={(e) => setHasBeenEvidenced(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#43C8F5] focus:ring-[#43C8F5]/40 shrink-0"
                        disabled={isLoading}
                      />
                      <span className="text-sm text-gray-500">Has this been evidenced?</span>
                    </label>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="inline-flex items-center justify-center rounded-full border border-[#43C8F5] bg-white px-10 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    >
                      {isLoading ? 'Saving...' : 'Submit'}
                    </button>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
                  )}
                </div>
              </form>
            ) : (
              <div className="flex-1 flex flex-col px-6 overflow-hidden">
                <div className="flex gap-3 py-4">
                  <div className="relative flex-1">
                    <select className="w-full appearance-none rounded-lg border border-[#E0DEE8] bg-white pl-4 pr-9 py-2 text-sm text-gray-700">
                      <option>Filter</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="relative flex-1">
                    <select className="w-full appearance-none rounded-lg border border-[#E0DEE8] bg-white pl-4 pr-9 py-2 text-sm text-gray-700">
                      <option>Sort</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 pb-4">
                  {innovationPlatformEntries.length === 0 ? (
                    <p className="text-sm text-gray-500 py-8 text-center">
                      No innovation platforms yet. Add your first one below.
                    </p>
                  ) : (
                    innovationPlatformEntries.map((platform) => {
                      const isExpanded = expandedPlatformId === platform.id;
                      const conceptLinks = platformConceptLinks[platform.id] ?? [];
                      return (
                        <div
                          key={platform.id}
                          ref={expandedPlatformId === platform.id ? platformCardRef : undefined}
                          className="rounded-xl border border-[#E0DEE8] bg-white overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedPlatformId(isExpanded ? null : platform.id)}
                            className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50/80"
                          >
                            <span className="text-sm font-bold text-gray-900 leading-snug pr-2">
                              {platformDisplayLabel(platform)}
                            </span>
                            <ChevronDown
                              className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {isExpanded && (
                            <div className="text-sm">
                              <div className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => handleViewWorkspaceClick(platform)}
                                  className={workspaceLinkUnboldClass}
                                >
                                  View Workspace
                                </button>
                              </div>

                              <div className="px-4 py-3">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-bold text-gray-900">Jobs to be done</p>
                                  <button
                                    type="button"
                                    onClick={() => void handleViewJobClick(platform)}
                                    className={workspaceLinkUnboldClass}
                                  >
                                    View
                                  </button>
                                </div>
                                <p className="text-gray-500 leading-relaxed">
                                  {platform.job_to_be_done?.trim() || '—'}
                                </p>
                              </div>

                              <div className="px-4 py-3 border-t border-[#EEEEEE]">
                                <p className="font-bold text-gray-900 mb-2">Tension</p>
                                <p className="text-gray-500 leading-relaxed">
                                  {platform.tension?.trim() || '—'}
                                </p>
                              </div>

                              {conceptLinks.length > 0 ? (
                                <div className="px-4 py-3 border-t border-[#EEEEEE]">
                                  <p className="font-bold text-gray-900 mb-2">Blueprints</p>
                                  <ul className="space-y-2">
                                    {conceptLinks.map((link) => (
                                      <li key={`${link.combinationId}-${link.artifactId}`}>
                                        <button
                                          type="button"
                                          onClick={() => handleViewConceptClick(platform, link)}
                                          className={`${workspaceLinkUnboldClass} text-left`}
                                        >
                                          {link.label}
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="py-4 border-t border-[#EEEEEE]">
                  <button
                    type="button"
                    onClick={() => {
                      resetPlatformForm();
                      setDrawerStep('platform-form');
                    }}
                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-xl border border-[#43C8F5] bg-white px-4 py-2.5 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors"
                  >
                    <Plus className="w-4 h-4 shrink-0" />
                    New innovation Platform
                  </button>
                </div>
              </div>
            )}
          </>
        )
      )}
    </div>

      {showOpportunityHelpModal && (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="opportunity-help-title"
        >
          <div
            className={nestedModalBackdropClass}
            onClick={isWorkspaceMode ? undefined : () => setShowOpportunityHelpModal(false)}
            aria-hidden
          />
          <div className={`${nestedModalPanelClass} p-6`}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 id="opportunity-help-title" className="text-lg font-bold text-gray-900">
                Need Help?
              </h2>
              <button
                type="button"
                onClick={() => setShowOpportunityHelpModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4 text-sm text-gray-500 leading-relaxed">
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut
                labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                laboris nisi ut aliquip ex ea commodo consequat.
              </p>
              <p>
                Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla
                pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt
                mollit anim id est laborum.
              </p>
            </div>
          </div>
        </div>
      )}

      {showJobsIdentifierModal && (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="jobs-identifier-title"
        >
          <div
            className={nestedModalBackdropClass}
            onClick={isWorkspaceMode ? undefined : closeJobsIdentifierModal}
            aria-hidden
          />
          <div
            className={`relative w-full max-w-xl rounded-2xl bg-white shadow-2xl ${
              isWorkspaceMode ? 'pointer-events-auto' : ''
            } max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="jobs-identifier-title" className="text-xl font-bold text-gray-900">
                  Jobs Identifier
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Consumers hire products to solve jobs; identifying those jobs enables intentional,
                  successful innovation.
                </p>
              </div>
              <button
                type="button"
                onClick={closeJobsIdentifierModal}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 min-w-0">
                  <select
                    id="jobs-identifier-market"
                    value={jobsIdentifierMarket}
                    onChange={(e) => {
                      setJobsIdentifierMarket(e.target.value);
                      if (jobsIdentifierError) setJobsIdentifierError(null);
                    }}
                    className={`${inputClass} appearance-none pr-10 cursor-pointer`}
                    disabled={jobsIdentifierBusy}
                  >
                    <option value="">Select market</option>
                    {projectMarkets.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <span className="text-sm text-gray-500 shrink-0">(Required)</span>
              </div>

              {jobsIdentifierError ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  {jobsIdentifierError}
                </div>
              ) : null}

              <div className="space-y-4">
                <CommercialProductList
                  products={workspaceProducts.jobs_identifier ?? []}
                  busy={workspaceProductsLoading || jobsIdentifierBusy}
                  onExplore={(product) =>
                    onNavigateToMarketplaceProduct?.(product.product_category, product.id)
                  }
                  onStart={() => {
                    setShowJobsIdentifierModal(false);
                    openAddJobModal();
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {showJobsPrioritiserModal && (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="jobs-prioritiser-title"
        >
          <div
            className={nestedModalBackdropClass}
            onClick={isWorkspaceMode ? undefined : closeJobsPrioritiserModal}
            aria-hidden
          />
          <div
            className={`relative w-full max-w-xl rounded-2xl bg-white shadow-2xl ${
              isWorkspaceMode ? 'pointer-events-auto' : ''
            } max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="jobs-prioritiser-title" className="text-xl font-bold text-gray-900">
                  Jobs Prioritiser
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Work with consumers to prioritise Jobs by importance and unmet need to target
                  highest-potential opportunities.
                </p>
              </div>
              <button
                type="button"
                onClick={closeJobsPrioritiserModal}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              <CommercialProductList
                products={workspaceProducts.jobs_prioritiser ?? []}
                busy={workspaceProductsLoading}
                onExplore={(product) =>
                  onNavigateToMarketplaceProduct?.(product.product_category, product.id)
                }
                onStart={(product) =>
                  onNavigateToMarketplaceProduct?.(product.product_category, product.id)
                }
              />
            </div>
          </div>
        </div>
      )}

      {showPlatformPrioritiserModal && (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="platform-prioritiser-title"
        >
          <div
            className={nestedModalBackdropClass}
            onClick={isWorkspaceMode ? undefined : closePlatformPrioritiserModal}
            aria-hidden
          />
          <div
            className={`relative w-full max-w-xl rounded-2xl bg-white shadow-2xl ${
              isWorkspaceMode ? 'pointer-events-auto' : ''
            } max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="platform-prioritiser-title" className="text-xl font-bold text-gray-900">
                  Platform Prioritiser
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  For when you have more Innovation Platforms (Job + Tension combinations) than you
                  can currently activate, so you want to prioritise your pipeline.
                </p>
              </div>
              <button
                type="button"
                onClick={closePlatformPrioritiserModal}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              <CommercialProductList
                products={workspaceProducts.platform_prioritiser ?? []}
                busy={workspaceProductsLoading}
                onExplore={(product) =>
                  onNavigateToMarketplaceProduct?.(product.product_category, product.id)
                }
                onStart={(product) =>
                  onNavigateToMarketplaceProduct?.(product.product_category, product.id)
                }
              />
            </div>
          </div>
        </div>
      )}

      {showPlatformPrioritiserConfirm && (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="platform-prioritiser-confirm-title"
        >
          <div className={nestedModalBackdropClass} aria-hidden />
          <div
            className={`relative w-full max-w-xl rounded-2xl bg-white shadow-2xl ${
              isWorkspaceMode ? 'pointer-events-auto' : ''
            } max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="platform-prioritiser-confirm-title" className="text-xl font-bold text-gray-900">
                  Platform Prioritiser
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  For when you have more Innovation Platforms (Job + Tension combinations) than you
                  can currently activate, so you want to prioritise your pipeline.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPlatformPrioritiserConfirm(false)}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              <div className="rounded-xl border border-[#E0DEE8] bg-white">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#EEEEEE]">
                  <span className="text-sm font-semibold text-gray-500">
                    {prioritiserSelectedCount} tension{prioritiserSelectedCount === 1 ? '' : 's'} selected
                  </span>
                  <button
                    type="button"
                    onClick={editPrioritiserSelection}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-[#43C8F5] text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors"
                    aria-label="Edit selection"
                    title="Edit selection"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {groupedPrioritiserSelections.map((group) => (
                    <div key={group.jobId} className="px-4 py-3 border-b border-[#EEEEEE] last:border-b-0">
                      <p className="text-sm font-bold text-gray-900 leading-snug">
                        {summarizeText(group.jobText, 120)}
                      </p>
                      <ul className="mt-2 space-y-1.5">
                        {group.tensions.map((tension) => (
                          <li key={tension.key} className="text-sm text-gray-600 leading-snug pl-3 border-l-2 border-[#E0DEE8]">
                            {tension.tensionText}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {PLATFORM_PRIORITISER_SERVICES.map((service) => (
                <div
                  key={service.id}
                  className="rounded-xl border border-[#E0DEE8] bg-white p-5 flex items-center justify-between gap-5"
                >
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-bold text-[#655FA8]">{service.title}</h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{service.description}</p>
                    <p className="mt-4 text-sm font-bold text-[#655FA8]">{service.pricing}</p>
                  </div>
                  <button
                    type="button"
                    className={`${workspacePurpleExploreClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                    onClick={() => void submitPlatformPrioritiserPurchase()}
                    disabled={prioritiserSaving}
                  >
                    {prioritiserSaving ? 'Saving…' : 'Explore'}
                  </button>
                </div>
              ))}

              {prioritiserError ? (
                <p className="text-sm text-red-600">{prioritiserError}</p>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {showTensionIdentifierModal && tensionIdentifierJob ? (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="tension-identifier-title"
        >
          <div
            className={nestedModalBackdropClass}
            onClick={isWorkspaceMode ? undefined : closeTensionIdentifierModal}
            aria-hidden
          />
          <div
            className={`relative w-full max-w-xl rounded-2xl bg-white shadow-2xl ${
              isWorkspaceMode ? 'pointer-events-auto' : ''
            } max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="min-w-0 pr-4">
                <h2 id="tension-identifier-title" className="text-xl font-bold text-gray-900">
                  Tension Identifier
                </h2>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">
                  Great products don&apos;t just meet a need—they solve a Tension. Find the gap
                  between what people want and what they currently get, and you&apos;ll find your
                  biggest opportunities.
                </p>
              </div>
              <button
                type="button"
                onClick={closeTensionIdentifierModal}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
                disabled={tensionIdentifierBusy}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              {tensionIdentifierError && !tensionIdentifierPendingService ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  {tensionIdentifierError}
                </div>
              ) : null}

              <CommercialProductList
                products={workspaceProducts.tension_identifier ?? []}
                busy={workspaceProductsLoading || tensionIdentifierBusy}
                onExplore={(product) =>
                  onNavigateToMarketplaceProduct?.(product.product_category, product.id)
                }
                onStart={() => {
                  const job = tensionIdentifierJob;
                  setShowTensionIdentifierModal(false);
                  if (!job) return;
                  setWorkspaceTab('jobs-to-be-done');
                  setExpandedJobId(job.id);
                  openAddTensionModal(job);
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {showTensionIdentifierModal &&
      tensionIdentifierJob &&
      tensionIdentifierPendingService &&
      pendingTensionIdentifierService ? (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="tension-identifier-confirm-title"
        >
          <div className={nestedModalBackdropClass} aria-hidden />
          <div className={`${nestedModalPanelClass} p-6`}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 id="tension-identifier-confirm-title" className="text-lg font-bold text-gray-900">
                Confirm generation
              </h2>
              <button
                type="button"
                onClick={cancelTensionIdentifierConfirm}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
                disabled={tensionIdentifierBusy}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              Are you sure you want to spend{' '}
              <span className="font-semibold text-gray-900">
                {pendingTensionIdentifierService.credits} credits
              </span>{' '}
              to generate tensions for this job to be done using{' '}
              <span className="font-semibold text-gray-900">
                {pendingTensionIdentifierService.title}
              </span>
              ?
            </p>

            {tensionIdentifierError ? (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {tensionIdentifierError}
              </div>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={cancelTensionIdentifierConfirm}
                className="px-5 py-2.5 rounded-full border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={tensionIdentifierBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmTensionIdentifierGenerate()}
                className="px-5 py-2.5 rounded-full bg-[#655FA8] text-white text-sm font-semibold hover:bg-[#544fa0] transition-colors inline-flex items-center gap-2"
                disabled={tensionIdentifierBusy}
              >
                {tensionIdentifierBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    Generating…
                  </>
                ) : (
                  'Yes, generate'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showJobsIdentifierModal && jobsIdentifierPendingService && pendingJobsIdentifierService ? (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="jobs-identifier-confirm-title"
        >
          <div className={nestedModalBackdropClass} aria-hidden />
          <div className={`${nestedModalPanelClass} p-6`}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 id="jobs-identifier-confirm-title" className="text-lg font-bold text-gray-900">
                Confirm generation
              </h2>
              <button
                type="button"
                onClick={cancelJobsIdentifierConfirm}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close"
                disabled={jobsIdentifierBusy}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              Are you sure you want to spend{' '}
              <span className="font-semibold text-gray-900">{pendingJobsIdentifierService.credits} credits</span>{' '}
              to generate 15 Jobs To Be Done for{' '}
              <span className="font-semibold text-gray-900">{jobsIdentifierMarket}</span> using{' '}
              <span className="font-semibold text-gray-900">{pendingJobsIdentifierService.title}</span>?
            </p>

            {jobsIdentifierError ? (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {jobsIdentifierError}
              </div>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={cancelJobsIdentifierConfirm}
                className="px-5 py-2.5 rounded-full border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={jobsIdentifierBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmJobsIdentifierGenerate()}
                className="px-5 py-2.5 rounded-full bg-[#655FA8] text-white text-sm font-semibold hover:bg-[#544fa0] transition-colors inline-flex items-center gap-2"
                disabled={jobsIdentifierBusy}
              >
                {jobsIdentifierBusy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    Generating…
                  </>
                ) : (
                  'Yes, generate'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showAddJobModal && (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="add-job-title"
        >
          <div
            className={nestedModalBackdropClass}
            onClick={isWorkspaceMode ? undefined : closeAddJobModal}
            aria-hidden
          />
          <div className={`${nestedModalPanelClass} max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <h2 id="add-job-title" className="text-lg font-bold text-gray-900">
                {editingJobId ? 'Edit Job to be done' : 'Add a Job to be done'}
              </h2>
              <button
                type="button"
                onClick={closeAddJobModal}
                className="p-1 text-gray-900 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitNewJob} className="px-6 pb-6 space-y-4">
              <div>
                <label htmlFor="job-when-part" className="block text-sm font-bold text-gray-900 mb-2">
                  When I&apos;m...<span className="text-gray-900">*</span>
                </label>
                <textarea
                  id="job-when-part"
                  value={jobWhenPart}
                  onChange={(e) => {
                    setJobWhenPart(e.target.value);
                    if (addJobError) setAddJobError(null);
                  }}
                  className={textareaClass}
                  placeholder="E.g: planning a Friday or Saturday night in with my partner or family."
                  disabled={isLoading}
                  rows={3}
                />
              </div>

              <div>
                <label htmlFor="job-want-part" className="block text-sm font-bold text-gray-900 mb-2">
                  I want...<span className="text-gray-900">*</span>
                </label>
                <textarea
                  id="job-want-part"
                  value={jobWantPart}
                  onChange={(e) => {
                    setJobWantPart(e.target.value);
                    if (addJobError) setAddJobError(null);
                  }}
                  className={textareaClass}
                  placeholder="E.g: a meal that feels more special than a regular meal."
                  disabled={isLoading}
                  rows={3}
                />
              </div>

              <div>
                <label htmlFor="job-so-can-part" className="block text-sm font-bold text-gray-900 mb-2">
                  So that...<span className="text-gray-900">*</span>
                </label>
                <textarea
                  id="job-so-can-part"
                  value={jobSoCanPart}
                  onChange={(e) => {
                    setJobSoCanPart(e.target.value);
                    if (addJobError) setAddJobError(null);
                  }}
                  className={textareaClass}
                  placeholder="E.g: we can enjoy sharing the time together."
                  disabled={isLoading}
                  rows={3}
                />
              </div>

              <div>
                <label htmlFor="job-market" className="block text-sm font-bold text-gray-900 mb-2">
                  Market<span className="text-gray-900">*</span>
                </label>
                <div className="relative">
                  <select
                    id="job-market"
                    value={jobMarket}
                    onChange={(e) => {
                      setJobMarket(e.target.value);
                      if (addJobError) setAddJobError(null);
                    }}
                    className={`${inputClass} appearance-none pr-10 cursor-pointer`}
                    disabled={isLoading}
                  >
                    <option value="">Select market</option>
                    {projectMarkets.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={jobHasBeenEvidenced}
                  onChange={(e) => setJobHasBeenEvidenced(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#43C8F5] focus:ring-[#43C8F5]/40"
                  disabled={isLoading}
                />
                <span className="text-sm text-gray-600">Has this been evidenced?</span>
              </label>

              {addJobError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  {addJobError}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center rounded-full border border-[#43C8F5] bg-white px-10 py-2 text-sm font-bold text-[#43C8F5] hover:bg-[#43C8F5]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addTensionForJob && (
        <div
          className={nestedModalShellClass}
          role="dialog"
          aria-modal
          aria-labelledby="add-tension-title"
        >
          <div
            className={nestedModalBackdropClass}
            onClick={isWorkspaceMode ? undefined : closeAddTensionModal}
            aria-hidden
          />
          <div className={nestedModalPanelClass}>
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <h2 id="add-tension-title" className="text-lg font-bold text-gray-900">
                {editingTensionId ? 'Edit Tension' : 'Add a Tension'}
              </h2>
              <button
                type="button"
                onClick={closeAddTensionModal}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitNewTension} className="px-6 pb-6">
              <div className="mb-4">
                <label htmlFor="tension-description" className="block text-sm font-bold text-gray-900 mb-2">
                  Add description<span className="text-gray-900">*</span>
                </label>
                <textarea
                  id="tension-description"
                  value={newTensionDescription}
                  onChange={(e) => {
                    setNewTensionDescription(e.target.value);
                    if (addTensionError) setAddTensionError(null);
                  }}
                  className={textareaClass}
                  placeholder="E.g: When I want to celebrate a small win, I want a drink that feels rewarding without feeling excessive"
                  disabled={isLoading}
                  rows={5}
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer mb-6">
                <input
                  type="checkbox"
                  checked={newTensionEvidenced}
                  onChange={(e) => setNewTensionEvidenced(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#43C8F5] focus:ring-[#43C8F5]/40"
                  disabled={isLoading}
                />
                <span className="text-sm text-gray-600">Has this been evidenced?</span>
              </label>

              {addTensionError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  {addTensionError}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#43C8F5] text-white text-sm font-medium rounded-full hover:bg-[#2A9CC4] transition-colors disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  {isLoading ? 'Adding...' : 'Add tension'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4 pointer-events-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-jtbd-title"
          onClick={() => {
            if (!deleting) setDeleteTarget(null);
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h2 id="delete-jtbd-title" className="text-lg font-bold text-gray-900">
                {deleteTarget.kind === 'job' ? 'Delete job to be done?' : 'Delete tension?'}
              </h2>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-5">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete{' '}
                <span className="font-medium text-gray-900">
                  {deleteTarget.kind === 'job'
                    ? deleteTarget.job.text
                    : deleteTarget.tension?.text}
                </span>
                ? This will remove it from the Jobs to be done list.
              </p>
              {deleteError && <p className="mt-3 text-sm text-red-600">{deleteError}</p>}
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteConfirm()}
                disabled={deleting}
                className="rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      <GatheringInfoToast
        message={prioritiserSuccessNotice}
        onClear={() => setPrioritiserSuccessNotice(null)}
      />
      {jobActionMenuPortal}
      {tensionActionMenuPortal}
    </>
  );
};

export default CreateProjectModal;
